/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { WelcomePopup } from '../components/tycoon/WelcomePopup';
import { MainMenuScreen } from '../components/tycoon/MainMenuScreen';
import { GameModals } from '../components/tycoon/GameModals';
import { ReactiveEventModal } from '../components/tycoon/events/ReactiveEventModal';
import { SystemicEventNotifications } from '../components/tycoon/events/SystemicEventNotification';
import { SettingsModal } from '../components/tycoon/SettingsModal';
import { GameDashboard } from '../components/tycoon/dashboard/GameDashboard';
import { SpatialGameLayout } from '../components/spatial/SpatialGameLayout';
import { useNavigationModeStore } from '../stores/navigationModeStore';
import { useSpatialNavigation } from '../hooks/useSpatialNavigation';
import { LOCATION_TO_TAB } from '../config/locationConfig';
import { EmployeeAvailabilityWarning } from '../components/tycoon/EmployeeAvailabilityWarning';
import { EmployeeAllocationModal } from '../components/tycoon/EmployeeAllocationModal';
// Import DesignerStudio component
import { DesignerStudio } from '../components/DesignerStudio';
// Import TutorialProvider for tutorial system
import { TutorialProvider } from '../tutorial/TutorialProvider';
// Import useTutorial hook for restart functionality
import { useTutorial } from '../hooks/useTutorial';

// Direct Zustand store imports
import { useGameStore } from '../stores/gameStore';
import { useCompanyStore } from '../stores/companyStore';
import { useProductStore } from '../stores/productStore';
import { useProductionStore } from '../stores/productionStore';
import { useMarketStore } from '../stores/marketStore';
import { useBuildingStore } from '../stores/buildingStore';
import { useInventoryStore } from '../stores/inventoryStore';
import { useUIStore } from '../stores/uiStore';
import { useSpatialStore } from '../stores/spatialStore';
import { useSupplyDemandHistoryStore } from '../stores/supplyDemandHistoryStore';
import { useFinancialHistoryStore } from '../stores/financialHistoryStore';

// Import grouped selectors
import {
  useGameState,
  useGameActions,
  useCompanyFinances,
  useCompanyMetrics,
  useCompanyActions,
  useCompanyData,
  useProductData,
  useProductActions,
  useMarketData,
  useBuildingData,
  useUIState,
  useGameTickUpdate
} from '../stores/selectors';

// Keep existing imports for services and types
import { playerActionService } from '../services/tycoon/playerActionService';
import { SalesCalculationService } from '../services/tycoon/salesCalculationService';
import { inventoryManagementService } from '../services/tycoon/inventoryManagementService';
import { autoOrderService } from '../services/tycoon/autoOrderService';
import { eventStore as eventService } from '../services/tycoon/eventStore';
import type { GameSave, PhaseProgressionPreferences } from '@smartphone-tycoon/shared-core';
import { useSaveGameStore } from '../stores/saveGameStore';
import { Product, ProductMetricsService } from '@smartphone-tycoon/shared-core';
import type { Product as ProductType, ProductReviews } from '@smartphone-tycoon/shared-core';
import type { ResearchState } from '@smartphone-tycoon/shared-core';
import packageJson from '../../package.json' with { type: 'json' };
import { GAME_CONFIG } from '../constants/gameConfig';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { audioService } from '../services/audioService';
import { devModeService } from '../services/devModeService';
import { buildingService } from '../services/tycoon/buildingService';
import { BUILDINGS_CONSTANTS, EMPLOYEE_CONSTANTS, GAME_CONSTANTS, DEVELOPMENT_CONSTANTS } from '../config/gameConstants';
import { GAME_FORMULAS } from '../config/gameFormulas';
import { reputationHistoryService } from '../services/reputationHistoryService';
import type { PlannedEmployeeAllocations, ProductDevelopmentPhase, DevelopmentPhase } from '../types/product';
import type { OwnedBuilding } from '../types/buildings';
import { employeeAllocationService } from '../services/tycoon/employeeAllocationService';
import type { EmployeeAllocation } from '../services/tycoon/employeeAllocationService';
import { factoryProductionService } from '../services/tycoon/factoryProductionService';
import { manufacturingService } from '../services/tycoon/manufacturingService';
import { v4 as uuidv4 } from 'uuid';
import { calculateMarketAppeal } from '../utils/businessCalculations';
import { getDiminishedQualityBonus, getEffectiveGameDate, getInvestmentPhaseUpdate, isStalePhaseEvent } from '../utils/phaseEventUtils';
import type { ActiveEvent } from '../types/dynamicEvents';
import { ProductManager } from '../services/productManager';
import { eventEngine } from '../services/tycoon/eventEngine';
import { reviewGenerator } from '../services/tycoon/reviewGeneratorService';
import { marketShareService } from '../services/tycoon/marketShareService';
// Keep only dynamic events hook (not part of state management migration)
import { useDynamicEvents } from '../stores/eventStore';

// Employee management actions now in companyStore

interface ProductionResult {
  success: boolean;
  error?: string;
  product?: Product;
}

interface TycoonModeProps {
  onBack: () => void;
  onDesignerStudio?: () => void;
  onCommunityHub?: () => void;
  onDevMode?: () => void;
  onGameStateUpdate?: (state: any) => void;
}

// Game speed configurations
const GAME_SPEEDS = [
  { name: 'Paused', multiplier: 0, tickRate: Infinity },
  { name: 'Normal', multiplier: 1, tickRate: 1000 },
  { name: 'Fast', multiplier: 2, tickRate: 500 },
  { name: 'Faster', multiplier: 4, tickRate: 250 },
  { name: 'Fastest', multiplier: 8, tickRate: 125 }
];


export function TycoonMode({ onCommunityHub, onDevMode, onGameStateUpdate }: TycoonModeProps) {
  const location = useLocation();
  const navigate = useNavigate();

  // Use grouped selectors to reduce subscriptions and improve performance
  const gameStateData = useGameState();
  const { gameDate, isPaused, simulationSpeed } = gameStateData;
  const { updateGameDate, pauseGame, resumeGame, setSimulationSpeed, resetGame } = useGameActions();

  const companyData = useCompanyData();
  const { research, employees, hiringProcesses } = companyData;
  const finances = useCompanyFinances();
  const { cash } = finances;
  const metrics = useCompanyMetrics();
  const { reputation, marketShare } = metrics;
  const researchPoints = research.researchPoints;

  const companyActions = useCompanyActions();
  const { setCash, updateMetrics, updateCompany, resetCompany, setResearchPoints,
    setEmployees, addEmployee, removeEmployee } = companyActions;

  // Get employee management actions from companyStore
  const { startHiring, fireEmployees, updateHiringProcesses, updateEmployeeMetrics,
    setHiringProcesses } = useCompanyStore();

  const productData = useProductData();
  const { products } = productData;
  const productActions = useProductActions();
  const { setProducts, createProduct, updateProduct, discontinueProduct, moveProductToNextPhase,
    startDevelopmentPhase, updateDevelopmentPhase, completeDevelopmentPhase, updateMultipleProducts } = productActions;

  const marketData = useMarketData();

  const uiState = useUIState();
  const { activeModals, openModal, closeModal } = uiState;

  // Use tutorial hook for restart functionality
  const { resetTutorial, startTutorial } = useTutorial();

  // Spatial navigation mode
  const navigationMode = useNavigationModeStore((state) => state.mode);
  const { currentLocation, currentTab: spatialCurrentTab, navigateToTab } = useSpatialNavigation();

  const buildingData = useBuildingData();
  const { buildings, initializeWithStartingBuilding, updateConstructionStatus, getMaintenanceCosts,
    getAllBuildingStats, getCapacityBreakdown } = buildingData;

  // These aren't in the grouped selectors yet, so keep individual subscriptions for now
  const setBuildings = useBuildingStore(state => state.setBuildings);
  const purchaseBuilding = useBuildingStore(state => state.purchaseBuilding);
  const sellBuilding = useBuildingStore(state => state.sellBuilding);
  const upgradeBuilding = useBuildingStore(state => state.upgradeBuilding);
  const checkCapacityForHiring = useBuildingStore(state => state.checkCapacityForHiring);

  // Get batched update function for game tick
  const gameTickUpdate = useGameTickUpdate();

  // Local state
  const [showWelcome, setShowWelcome] = useState(true);
  const [showNewGame, setShowNewGame] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<{ ceoName: string; companyName: string } | null>(null);
  const [showBankruptcyWarning, setShowBankruptcyWarning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [currentSlotId, setCurrentSlotId] = useState<number | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [dashboardActiveTab, setDashboardActiveTab] = useState('Headquarters');
  const [phasePreferences, setPhasePreferences] = useState<PhaseProgressionPreferences>(() => {
    // Load preferences from localStorage
    const savedPrefs = localStorage.getItem('smartphone-simulator-preferences');
    if (savedPrefs) {
      const prefs = JSON.parse(savedPrefs);
      if (prefs.phasePreferences) {
        return prefs.phasePreferences;
      }
    }
    // Default preferences
    return {
      design_complete: 'ask',
      engineering_complete: 'ask',
      testing_complete: 'ask',
      manufacturing_complete: 'ask'
    };
  });

  const [phasePreferencesLastChanged, setPhasePreferencesLastChanged] = useState<Date | null>(null);
  const [releaseModalOpen, setReleaseModalOpen] = useState(false);
  const [productToRelease, setProductToRelease] = useState<ProductType | null>(null);
  const [productReviews, setProductReviews] = useState<ProductReviews>({ professional: [], customer: [] });
  const [discontinueModalOpen, setDiscontinueModalOpen] = useState(false);
  const [productToDiscontinue, setProductToDiscontinue] = useState<ProductType | null>(null);
  const [currentResearchState, setCurrentResearchState] = useState<ResearchState | null>(null);
  const [researchUnlocks, setResearchUnlocks] = useState<any[]>([]);
  const [designerRouteState, setDesignerRouteState] = useState<{ editProduct?: any } | null>(null);
  const [audioSettings, setAudioSettings] = useState(() => {
    // Load audio settings from localStorage
    const savedPrefs = localStorage.getItem('smartphone-simulator-preferences');
    if (savedPrefs) {
      const prefs = JSON.parse(savedPrefs);
      if (prefs.musicEnabled !== undefined) {
        return {
          musicEnabled: prefs.musicEnabled,
          musicVolume: prefs.musicVolume || 0.5,
          currentTrack: prefs.currentTrack || 0
        };
      }
    }
    // Default settings
    return {
      musicEnabled: true,
      musicVolume: 0.5,
      currentTrack: 0
    };
  });

  // Employee allocation modal states
  const [showEmployeeWarning, setShowEmployeeWarning] = useState(false);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [modalKey, setModalKey] = useState(0); // Force modal to re-mount with fresh props
  const [missingEmployees, setMissingEmployees] = useState<Record<string, number>>({});
  const [pendingDesignConfig, setPendingDesignConfig] = useState<any>(null);

  // Game events state (will be replaced with event store later)
  const [events, setEvents] = useState<any[]>([]);
  const [bankruptcyWarnings, setBankruptcyWarnings] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);

  // Fire warning preference (UI only)
  const [showFireWarning, setShowFireWarning] = useState(() => {
    const savedPrefs = localStorage.getItem('smartphone-simulator-preferences');
    if (savedPrefs) {
      const prefs = JSON.parse(savedPrefs);
      if (prefs.fireWarningEnabled !== undefined) {
        return prefs.fireWarningEnabled;
      }
    }
    return true;
  });
  const [employeeAllocations, setEmployeeAllocations] = useState<any[]>([]);
  // Use dailyStats from company store instead of local state
  const dailyStats = finances.dailyStats;

  const saveGame = useSaveGameStore(state => state.saveGame);

  // Initialize music when game starts
  useEffect(() => {
    if (!showWelcome && companyInfo) {
      // Start playing music when game begins
      audioService.applySettings(audioSettings);
      if (audioSettings.musicEnabled) {
        audioService.play();
      }
    } else {
      // Stop music when returning to menu
      audioService.stop();
    }

    return () => {
      // Cleanup: pause music when component unmounts
      audioService.pause();
    };
  }, [showWelcome, companyInfo, audioSettings.musicEnabled]);

  // Dynamic Events System (temporary compatibility)
  const { data: eventsData, actions: eventsActions } = useDynamicEvents();

  // Add event function
  const addEvent = useCallback((event: any) => {
    setEvents(prev => [...prev, {
      ...event,
      timestamp: new Date(),
      gameDate: gameDateRef.current,
      id: event.id || `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }]);
  }, []);

  // Only pause for critical system modals - never pause for tab navigation or UI states
  const isAnyModalOpen = showSettings || showBankruptcyWarning || showAllocationModal || showEmployeeWarning || location.pathname === '/designer';

  // Initialize with empty array first, will be updated after product pipeline is initialized
  const [activeProductsForSimulation, setActiveProductsForSimulation] = useState<any[]>([]);

  // Keep refs to avoid closure issues in game loop
  const latestAllocationsRef = useRef<any[]>([]);
  const gameDateRef = useRef(gameDate);
  const activeProductsRef = useRef(activeProductsForSimulation);
  const employeesRef = useRef(employees);
  const buildingsRef = useRef(buildings);

  // Track monthly financial accumulation for historical chart
  const monthlyAccumulatorRef = useRef({ revenue: 0, costs: 0 });

  // Track whether game was paused before entering designer (to restore correct state on exit)
  const wasPausedBeforeDesignerRef = useRef(false);

  // Track triggered phase events to prevent double-triggering during React state updates
  const triggeredPhaseEventsRef = useRef<Set<string>>(new Set());
  const lastPhasePromptAtRef = useRef<Map<string, number>>(new Map());

  // Track currently processing event to prevent multi-clicks on the same modal
  const processingEventRef = useRef<string | null>(null);

  const PHASE_EVENT_RETRIGGER_COOLDOWN_MS = 250;

  // Track if we paused due to reactive event (to know whether to resume after handling)
  const pausedForReactiveEventRef = useRef(false);

  // Update refs when values change
  useEffect(() => {
    gameDateRef.current = gameDate;
  }, [gameDate]);

  useEffect(() => {
    activeProductsRef.current = activeProductsForSimulation;
  }, [activeProductsForSimulation]);

  useEffect(() => {
    employeesRef.current = employees;
    // console.log(`[${new Date().toISOString()}] [TycoonMode] Employees ref updated:`, employees);
  }, [employees]);

  useEffect(() => {
    buildingsRef.current = buildings;
    // console.log(`[${new Date().toISOString()}] [TycoonMode] Buildings ref updated:`, buildings);
  }, [buildings]);

  useEffect(() => {
    if (!processingEventRef.current) {
      return;
    }

    const hasPendingMatch = eventsData.pendingReactiveEvents?.some(pending => {
      const pendingKey = `${pending.event.id}-${pending.productContext?.productId ?? 'global'}`;
      return pendingKey === processingEventRef.current;
    });

    if (!hasPendingMatch) {
      processingEventRef.current = null;
    }
  }, [eventsData.pendingReactiveEvents]);

  // Keep ref for onGameStateUpdate to avoid it in dependencies
  const onGameStateUpdateRef = useRef(onGameStateUpdate);
  useEffect(() => {
    onGameStateUpdateRef.current = onGameStateUpdate;
  }, [onGameStateUpdate]);

  // Create a ref to hold the updateProductSales function
  const updateProductSalesRef = useRef<((sales: Array<{ productId: string, unitsSold: number, revenue: number }>) => void) | null>(null);

  // Simulation speed control
  const speed = useMemo(() =>
    GAME_SPEEDS.find(s => s.multiplier === simulationSpeed) || GAME_SPEEDS[1],
    [simulationSpeed]
  );

  const togglePause = useCallback(() => {
    if (isPaused) {
      resumeGame();
    } else {
      pauseGame();
    }
  }, [isPaused, pauseGame, resumeGame]);

  const changeSpeed = useCallback((newSpeed: typeof GAME_SPEEDS[0]) => {
    setSimulationSpeed(newSpeed.multiplier);
    if (newSpeed.multiplier > 0) {
      resumeGame();
    }
  }, [setSimulationSpeed, resumeGame]);

  // Toggle fire warning preference
  const toggleFireWarning = useCallback((show: boolean) => {
    setShowFireWarning(show);
    // Update preferences structure
    const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
    const prefs = JSON.parse(savedPrefs);
    prefs.fireWarningEnabled = show;
    localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));
  }, []);

  const setPaused = useCallback((paused: boolean) => {
    if (paused) {
      pauseGame();
    } else {
      resumeGame();
    }
  }, [pauseGame, resumeGame]);

  // Memoized callback for clearing error state - prevents ReactiveEventModal useEffect from resetting selection
  const handleClearError = useCallback(() => setError(null), []);

  const resolveReactiveEventChoice = useCallback((pendingEvent: ActiveEvent, choiceId: string) => {
    try {
      eventEngine.playerChoice(pendingEvent, choiceId);
      const resolvedEvent = eventEngine.getEventHistory().find(eventEntry =>
        eventEntry.event.id === pendingEvent.event.id &&
        eventEntry.status === 'resolved' &&
        eventEntry.productContext?.productId === pendingEvent.productContext?.productId
      );

      if (resolvedEvent) {
        eventService.updateEventWithResolution(resolvedEvent);
      }
    } catch (error) {
      console.error('[TycoonMode] Failed to resolve reactive event choice', error);
    } finally {
      eventService.handlePlayerChoice(pendingEvent.event.id);
    }
  }, []);

  // Combined state for compatibility
  const state = useMemo(() => {
    // console.log(`[TycoonMode.state] Creating state object with cash: ${finances.cash}`);
    return {
      cash: finances.cash,
      reputation,
      marketShare,
      researchPoints,
      day: gameDate.day,
      month: gameDate.month,
      year: gameDate.year,
      isGameOver,
      bankruptcyWarnings,
      dailyStats,
      marketData,
      research,
      employees,
      hiringProcesses,
      buildings,
      employeeAllocations
    };
  }, [finances.cash, reputation, marketShare, researchPoints, gameDate, isGameOver, bankruptcyWarnings,
    dailyStats, marketData, research, employees, hiringProcesses, buildings, employeeAllocations]);

  // Wrapper for updateState that updates multiple stores
  const updateState = useCallback((updates: any) => {
    if ('cash' in updates) {
      setCash(updates.cash);
    }
    if ('reputation' in updates || 'marketShare' in updates) {
      updateMetrics({
        ...(updates.reputation !== undefined && { reputation: updates.reputation }),
        ...(updates.marketShare !== undefined && { marketShare: updates.marketShare })
      });
    }
    if ('researchPoints' in updates) {
      setResearchPoints(updates.researchPoints);
    }
    if ('employees' in updates) {
      setEmployees(updates.employees);
    }
    if ('buildings' in updates) {
      setBuildings(updates.buildings);
    }
    if ('employeeAllocations' in updates) {
      setEmployeeAllocations(updates.employeeAllocations);
      latestAllocationsRef.current = updates.employeeAllocations;
    }
    if ('hiringProcesses' in updates) {
      setHiringProcesses(updates.hiringProcesses);
    }
    // dailyStats now comes from company store, no need to update locally

    // Update dev mode if connected - removed to prevent circular updates
  }, [setCash, updateMetrics, setResearchPoints, setEmployees, setBuildings,
    setEmployeeAllocations, setHiringProcesses]);
  // Remove state and other values from dependencies - only include setters

  // Listen for preference changes from debug commands
  useEffect(() => {
    const handlePreferenceChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail.type === 'phasePreferences') {
        setPhasePreferences(customEvent.detail.value);
        setPhasePreferencesLastChanged(new Date());

        // Sync with event engine
        if (companyInfo) {
          import('../services/tycoon/eventEngine').then(({ eventEngine }) => {
            eventEngine.setPhasePreferences(customEvent.detail.value, new Date());
          });
        }
      }
    };

    window.addEventListener('preferenceChanged', handlePreferenceChange);
    return () => window.removeEventListener('preferenceChanged', handlePreferenceChange);
  }, [companyInfo]);

  // Initialize buildings when game starts
  useEffect(() => {
    if (!showWelcome && companyInfo && buildings.length === 0) {
      initializeWithStartingBuilding(gameDate);
    }
  }, [showWelcome, companyInfo, buildings.length]); // Removed gameDate from dependencies

  // Update construction status daily
  useEffect(() => {
    if (!showWelcome && companyInfo && !isPaused) {
      updateConstructionStatus(gameDate);
    }
  }, [gameDate.day, showWelcome, companyInfo, isPaused, gameDate]); // Keep gameDate.day as it's needed to trigger daily

  // Product pipeline functions using store actions
  const calculateDevelopmentCost = (designConfig: any): number => {
    const componentsCost = ProductMetricsService.calculateProductionCost(designConfig.components);
    return 100000 + (componentsCost * 0.2);
  };

  const startDevelopment = useCallback(async (
    designConfig: any,
    cashAmount: number,
    updateCashFunction: (newCash: number) => void,
    gameDateParam: { day: number; month: number; year: number },
    globalPreferences?: PhaseProgressionPreferences
  ): Promise<ProductionResult> => {
    try {
      const developmentCost = calculateDevelopmentCost(designConfig);

      if (cashAmount < developmentCost) {
        return {
          success: false,
          error: `Insufficient funds. Required: ${formatCurrency(developmentCost)}`
        };
      }

      // Calculate tech score to determine employee requirements
      const techScore = ProductMetricsService.calculateTechScore(designConfig.components);

      // Check employee availability for design stage
      const employeeCheck = employeeAllocationService.canAllocateEmployees(
        'design',
        techScore,
        gameDateParam.year,
        employees,
        employeeAllocations
      );

      if (!employeeCheck.canAllocate) {
        return {
          success: false,
          error: employeeCheck.reason
        };
      }

      const marketAppeal = calculateMarketAppeal(techScore, designConfig.price);

      const newProduct: Product = {
        id: `product-${uuidv4()}`,
        name: designConfig.name || 'New Phone',
        description: 'A revolutionary new device',
        developmentCost,
        productionCost: ProductMetricsService.calculateProductionCost(designConfig.components),
        price: designConfig.price || 0,
        techScore,
        marketAppeal,
        status: 'design',
        designConfig,
        metadata: {
          companyId: 'player',
          designDate: gameDateParam
        },
        salesMetrics: {
          unitsSold: 0,
          revenue: 0,
          averageRating: 5.0,
          marketShare: 0
        },
        developmentPhase: {
          phase: 'design',
          startDate: gameDateParam,
          estimatedDuration: 7, // PHASE_DURATIONS.design
          budget: developmentCost,
          confidence: DEVELOPMENT_CONSTANTS.initialConfidence,
          qualityScore: DEVELOPMENT_CONSTANTS.initialQuality,
          riskEvents: [],
          investmentCount: 0
        }
      };

      // Check if design config includes planned allocations
      if (designConfig.plannedAllocations) {
        // Store planned allocations with the product
        newProduct.plannedAllocations = designConfig.plannedAllocations;

        // Use planned allocation for design phase
        const designAllocation = designConfig.plannedAllocations.design;
        if (designAllocation && newProduct.developmentPhase) {
          newProduct.developmentPhase.allocatedEmployees = designAllocation;
        }

        // Create allocation records for tracking
        const allocationRecords: EmployeeAllocation[] = [];
        Object.entries(designAllocation || {}).forEach(([employeeType, count]) => {
          if (count > 0) {
            allocationRecords.push({
              productId: newProduct.id,
              phase: 'design',
              employeeType,
              count
            });
          }
        });

        // Update employee allocations
        const newAllocations = [...employeeAllocations, ...allocationRecords];
        setEmployeeAllocations(newAllocations);
      } else {
        // Fallback to automatic allocation if no planned allocations
        const allocation = employeeAllocationService.allocateEmployees(
          newProduct.id,
          'design',
          techScore,
          gameDateParam.year,
          employeeAllocations
        );

        // Update employee allocations
        const newAllocations = [...employeeAllocations, allocation];
        setEmployeeAllocations(newAllocations);

        // Add allocation info to development phase
        if (newProduct.developmentPhase) {
          newProduct.developmentPhase.allocatedEmployees = {
            [allocation.employeeType]: allocation.count
          };
        }
      }

      // Update products in store
      createProduct(newProduct);
      updateCashFunction(cashAmount - developmentCost);

      // Save to database
      try {
        // Remove the id before saving to database since it will auto-generate one
        const { id, ...productDataForDb } = newProduct;
        const result = await ProductManager.createProduct(productDataForDb);
        if (!result.success) {
          console.error('Failed to save product to database:', result.error);
          // Return failure if database save fails
          return {
            success: false,
            error: 'Failed to save product to database'
          };
        }
        // Update the product with the database-generated ID if available
        if (result.data && result.data.id) {
          newProduct.id = result.data.id;
        }
      } catch (error) {
        console.error('Error saving product to database:', error);
        return {
          success: false,
          error: 'Error saving product to database'
        };
      }

      return { success: true, product: newProduct };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to start development'
      };
    }
  }, [employees, employeeAllocations, setEmployeeAllocations, createProduct]);

  const startProduction = useCallback((productId: string) => {
    updateProduct(productId, { status: 'production' });
  }, [updateProduct]);

  const pipelineDiscontinue = useCallback((productId: string) => {
    discontinueProduct(productId, gameDate);
  }, [discontinueProduct, gameDate]);

  const updateProductSales = useCallback((sales: Array<{ productId: string, unitsSold: number, revenue: number }>) => {
    // Update only unitsSold and revenue here
    // Market share is calculated separately by marketShareService to include all products (player + competitor)
    const updates = sales.map(sale => {
      const product = products.find(p => p.id === sale.productId);
      return {
        id: sale.productId,
        updates: {
          salesMetrics: {
            unitsSold: (product?.salesMetrics?.unitsSold || 0) + sale.unitsSold,
            revenue: (product?.salesMetrics?.revenue || 0) + sale.revenue,
            averageRating: product?.salesMetrics?.averageRating || 0,
            marketShare: product?.salesMetrics?.marketShare || 0  // Keep existing, calculated by marketShareService
          }
        }
      };
    });
    updateMultipleProducts(updates);
  }, [products, updateMultipleProducts]);

  const pipelineUpdateProduct = updateProduct;
  const pipelineSetProducts = setProducts;

  // Progress phases for all products in development
  const progressPhases = useCallback((currentDate: typeof gameDate) => {
    // Track pending allocations within this tick to prevent over-allocation
    const pendingAllocations: EmployeeAllocation[] = [];
    const latestProducts = useProductStore.getState().products;

    latestProducts.forEach(product => {
      if (product.developmentPhase && product.status !== 'production' && product.status !== 'discontinued') {
        const daysSince = calculateDaysSince(product.developmentPhase.startDate, currentDate);
        const progress = Math.min(100, (daysSince / product.developmentPhase.estimatedDuration) * 100);

        // Only update if progress has actually changed (rounded to avoid floating point issues)
        const currentProgress = Math.round(product.developmentPhase.progress || 0);
        const newProgress = Math.round(progress);

        // Check if phase is complete (before any state updates)
        const phaseComplete = progress >= 100 && !product.developmentPhase.isComplete;
        let shouldTriggerPhaseEvent = false;
        let shouldMarkComplete = phaseComplete;

        // Check for duplicate event trigger BEFORE any state updates
        if (phaseComplete) {
          const eventKey = `${product.id}-${product.status}`;
          if (triggeredPhaseEventsRef.current.has(eventKey)) {
            console.log(`[TycoonMode] Skipping duplicate event trigger for ${product.name} in ${product.status} phase`);
            shouldMarkComplete = true;
          } else {
            const now = Date.now();
            const lastPromptAt = lastPhasePromptAtRef.current.get(eventKey) || 0;
            if (now - lastPromptAt < PHASE_EVENT_RETRIGGER_COOLDOWN_MS) {
              // Defer re-prompt so the modal can fully close before re-opening
              shouldMarkComplete = false;
            } else {
              triggeredPhaseEventsRef.current.add(eventKey);
              lastPhasePromptAtRef.current.set(eventKey, now);
              shouldTriggerPhaseEvent = true;
              shouldMarkComplete = true;
            }
          }
        }

        // Single atomic update for both progress and isComplete
        // This prevents race conditions where two separate updateProduct calls
        // use stale closure values and overwrite each other
        if (currentProgress !== newProgress || shouldMarkComplete) {
          updateProduct(product.id, {
            developmentPhase: {
              ...product.developmentPhase,
              progress,
              ...(shouldMarkComplete && { isComplete: true })
            }
          });
        }

        // Trigger event AFTER state update
        if (shouldTriggerPhaseEvent) {
          // Build game state for event engine
          const tycoonGameState = {
            cash,
            reputation,
            marketShare,
            researchPoints,
            day: currentDate.day,
            month: currentDate.month,
            year: currentDate.year,
            products: latestProducts
          };

          // Trigger the event system - it will handle preferences (ask/proceed/minor/major)
          // If preference is 'ask', it will create an event that shows the ReactiveEventModal
          // If preference is 'proceed'/'minor'/'major', it will auto-handle via productProgressionCallback
          console.log(`[TycoonMode] Phase ${product.status} complete for "${product.name}" - triggering event system`);
          eventEngine.triggerPhaseCompleteEvent(
            product.id,
            product.name,
            product.status,
            tycoonGameState,
            product
          );
        }
      }
    });

    // Note: Employee allocation is now handled by advanceProductToNextPhase
    // which is called from productProgressionCallback or handlePhaseEventChoice
  }, [updateProduct, cash, reputation, marketShare, researchPoints]);

  const progressProductToPhase = moveProductToNextPhase;

  const applyPhaseInvestment = useCallback((productId: string, investment: {
    additionalDays?: number;
    confidence?: number;
    qualityScore?: number;
    techScore?: number;
    marketAppeal?: number;
    productionCostReduction?: number;
  }, currentDateOverride?: typeof gameDate) => {
    const product = useProductStore.getState().products.find(p => p.id === productId);
    if (!product?.developmentPhase) return;

    const currentPhase = product.developmentPhase;
    const currentInvestmentCount = currentPhase.investmentCount || 0;
    const diminishedQualityGain = getDiminishedQualityBonus(investment.qualityScore || 0, currentInvestmentCount);
    const effectiveDate = getEffectiveGameDate(currentDateOverride, gameDateRef.current);
    const investmentUpdate = getInvestmentPhaseUpdate({
      currentPhase: currentPhase as {
        startDate: typeof gameDate;
        estimatedDuration: number;
        progress?: number;
        daysElapsed?: number;
      },
      additionalDays: investment.additionalDays,
      currentDate: effectiveDate
    });

    // Update the product's development phase directly (NOT developmentPhases store!)
    // Must use updateProduct because progressPhases reads from product.developmentPhase
    updateProduct(productId, {
      developmentPhase: {
        ...currentPhase,
        estimatedDuration: investmentUpdate.estimatedDuration,
        estimatedDays: investmentUpdate.estimatedDuration,
        startDate: investmentUpdate.startDate,
        progress: investmentUpdate.progress,  // Reset progress based on new duration
        confidence: Math.min(100, (currentPhase.confidence || 0) + (investment.confidence || 0)),
        qualityScore: Math.min(100, (currentPhase.qualityScore || 0) + diminishedQualityGain),
        investmentCount: currentInvestmentCount + 1,
        isComplete: false  // Critical: Reset so phase can complete again
      },
      // Also update product-level metrics if applicable
      ...(investment.techScore && { techScore: (product.techScore || 0) + investment.techScore }),
      ...(investment.marketAppeal && { marketAppeal: (product.marketAppeal || 0) + investment.marketAppeal })
    });

    // Allow the phase to re-trigger once the extended duration completes
    const eventKey = `${productId}-${product.status}`;
    triggeredPhaseEventsRef.current.delete(eventKey);

    console.log(`[TycoonMode] Applied investment to "${product.name}": +${investment.additionalDays || 0} days, phase extended to ${investmentUpdate.estimatedDuration} days, progress reset to ${investmentUpdate.progress.toFixed(1)}%`);
  }, [updateProduct]);

  const getEffectivePhaseStartDate = useCallback((requestedDate: { day: number; month: number; year: number }) => {
    return getEffectiveGameDate(requestedDate, gameDateRef.current);
  }, []);

  // Helper function to advance a product to the next phase with full employee allocation logic
  const advanceProductToNextPhase = useCallback((
    product: ProductType,
    nextStatus: ProductType['status'],
    currentDate: typeof gameDate,
    pendingAllocations: EmployeeAllocation[] = []
  ): { success: boolean; newAllocation?: EmployeeAllocation; reason?: string } => {
    const phaseStartDate = getEffectivePhaseStartDate(currentDate);

    // For production phase, no employees needed - just transition
    // Keep developmentPhase to preserve qualityScore for released product display
    if (nextStatus === 'production') {
      updateProduct(product.id, {
        status: 'production'
      });
      console.log(`[TycoonMode] Product "${product.name}" advanced to production phase`);
      return { success: true };
    }

    // Check employee availability for development phases
    const techScore = product.techScore || 0;
    const currentAllocations = [...employeeAllocations, ...pendingAllocations];
    const employeeCheck = employeeAllocationService.canAllocateEmployees(
      nextStatus as DevelopmentPhase,
      techScore,
      currentDate.year,
      employees,
      currentAllocations
    );

    if (!employeeCheck.canAllocate) {
      const requiredType = employeeAllocationService.getRequiredEmployeeType(nextStatus as DevelopmentPhase);
      return {
        success: false,
        reason: `Need ${employeeCheck.required} ${requiredType}(s) to advance to ${nextStatus} phase. ${employeeCheck.reason || ''}`
      };
    }

    // Allocate employees for the next phase
    const newAllocation = employeeAllocationService.allocateEmployees(
      product.id,
      nextStatus as DevelopmentPhase,
      techScore,
      currentDate.year,
      currentAllocations
    );

    // Update employee allocations
    setEmployeeAllocations(prev => [...prev, newAllocation]);

    // Create new development phase with proper settings
    const phaseDurations: Record<string, number> = {
      'engineering': 10,
      'testing': 10,
      'manufacturing': 10
    };

    const newPhase: ProductDevelopmentPhase = {
      phase: nextStatus as DevelopmentPhase,
      startDate: phaseStartDate,
      estimatedDuration: phaseDurations[nextStatus] || 10,
      budget: product.developmentPhase?.budget || 0,
      confidence: DEVELOPMENT_CONSTANTS.initialConfidence,
      qualityScore: DEVELOPMENT_CONSTANTS.initialQuality,
      riskEvents: [],
      investmentCount: 0,
      progress: 0,
      isComplete: false,
      investment: 0,
      estimatedDays: phaseDurations[nextStatus] || 10
    };

    updateProduct(product.id, {
      status: nextStatus,
      developmentPhase: newPhase
    });

    console.log(`[TycoonMode] Product "${product.name}" advanced to ${nextStatus} phase with ${newAllocation.count} ${newAllocation.employeeType}(s) allocated`);
    return { success: true, newAllocation };
  }, [employees, employeeAllocations, setEmployeeAllocations, updateProduct, getEffectivePhaseStartDate]);

  const releaseProduct = useCallback((productId: string, reviews?: ProductReviews) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    // Use provided reviews or generate new ones
    const productReviewsToUse = reviews || reviewGenerator.generateReviews(product);
    const averageRating = reviewGenerator.calculateAverageRating(productReviewsToUse);

    updateProduct(productId, {
      status: 'released',
      reviews: productReviewsToUse,
      salesMetrics: {
        ...product.salesMetrics,
        averageRating
      },
      metadata: {
        ...product.metadata,
        releaseDate: gameDate,
        isReleased: true
      }
    });
  }, [updateProduct, gameDate, products]);

  // Utility function wrapper for date calculations
  const calculateDaysSince = (
    startDate: { day: number; month: number; year: number },
    currentDate: { day: number; month: number; year: number }
  ): number => {
    return GAME_FORMULAS.time.getDaysBetween(startDate, currentDate);
  };

  const getPhaseProgress = useCallback((productId: string) => {
    const product = products.find(p => p.id === productId);
    if (!product?.developmentPhase) {
      return { progress: 100, daysRemaining: 0, totalDays: 0 };
    }

    const daysSince = calculateDaysSince(product.developmentPhase.startDate, gameDate);
    const totalDays = product.developmentPhase.estimatedDuration;
    const progress = Math.min(100, (daysSince / totalDays) * 100);
    const daysRemaining = Math.max(0, totalDays - daysSince);

    return { progress, daysRemaining, totalDays };
  }, [products, gameDate]);

  const isPhaseComplete = useCallback((productId: string) => {
    const product = products.find(p => p.id === productId);
    return product?.developmentPhase?.isComplete || false;
  }, [products]);

  const getProductAllocations = useCallback((productId: string) => {
    return employeeAllocations.filter(alloc => alloc.productId === productId);
  }, [employeeAllocations]);

  const isProductStagePaused = useCallback((productId: string) => {
    const product = products.find(p => p.id === productId);
    return product?.developmentPhase?.isPaused || false;
  }, [products]);


  // Employee management compatibility object
  // const employeeManagement = useMemo(() => ({
  //   data: {
  //     employees,
  //     hiringProcesses,
  //     showFireWarning
  //   },
  //   actions: {
  //     hire: hireEmployee,
  //     fire: fireEmployee,
  //     setEmployees,
  //     setHiringProcesses: updateHiringProcesses,
  //     toggleFireWarning
  //   }
  // }), [employees, hiringProcesses, showFireWarning, hireEmployee, fireEmployee, setEmployees, toggleFireWarning]);

  // Building management compatibility object
  const buildingManagement = useMemo(() => ({
    data: {
      buildings
    },
    actions: {
      purchaseBuilding: (buildingTypeId: string) =>
        purchaseBuilding(buildingTypeId, gameDate, cash, reputation, setCash, addEvent),
      sellBuilding: (buildingId: string) =>
        sellBuilding(buildingId, employees, cash, setCash, addEvent),
      upgradeBuilding: (buildingId: string, upgradeType: string) =>
        upgradeBuilding(buildingId, upgradeType, gameDate, cash, reputation, setCash, addEvent),
      checkCapacityForHiring,
      getCapacityBreakdown,
      getMaintenanceCosts,
      getAllBuildingStats
    }
  }), [buildings, purchaseBuilding, sellBuilding, upgradeBuilding, checkCapacityForHiring,
    getCapacityBreakdown, getMaintenanceCosts, getAllBuildingStats, gameDate, cash,
    reputation, setCash, addEvent, employees]);

  // No longer needed - using stores directly

  // Update ref when employeeAllocations changes
  useEffect(() => {
    latestAllocationsRef.current = employeeAllocations;
  }, [employeeAllocations]);

  // Update the ref with the actual updateProductSales function
  useEffect(() => {
    updateProductSalesRef.current = updateProductSales;
  }, [updateProductSales]);

  // Update active products for simulation whenever products change
  useEffect(() => {
    const activeProducts = products.filter(p =>
      p.status === 'released'
    );
    setActiveProductsForSimulation(activeProducts);
  }, [products]);

  // Clear products on initial mount unless loading from save
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    const timestamp = new Date().toISOString();
    // console.log(`[${timestamp}] [TycoonMode.initialMount] Effect running, showWelcome: ${showWelcome}, currentSlotId: ${currentSlotId}, hasInitialized: ${hasInitializedRef.current}`);

    if (!hasInitializedRef.current && showWelcome && !currentSlotId) {
      hasInitializedRef.current = true;
      const currentCash = useCompanyStore.getState().finances?.cash;
      // console.log(`[${timestamp}] [TycoonMode.initialMount] 🧹 Initial mount - current cash: ${currentCash}`);
      // console.log(`[${timestamp}] [TycoonMode.initialMount] Resetting all game state for clean start...`);

      // Reset all game state on initial mount
      import('../services/tycoon/gameStateResetManager').then(({ GameStateResetManager }) => {
        const resetTimestamp = new Date().toISOString();
        // console.log(`[${resetTimestamp}] [TycoonMode.initialMount] About to call GameStateResetManager.resetAllGameState()`);

        GameStateResetManager.resetAllGameState().then(() => {
          const afterResetCash = useCompanyStore.getState().finances?.cash;
          // console.log(`[${new Date().toISOString()}] [TycoonMode.initialMount] ✅ All game state reset on mount, cash is now: ${afterResetCash}`);
        });
      });

      // Clear player products
      setProducts([]);
      setProducts([]);
      // console.log(`[${timestamp}] [TycoonMode.initialMount] ✅ Player products cleared on mount`);
    }
  }, []); // Empty dependency array - only runs on mount

  // Initialize debug service with game refs
  useEffect(() => {
    if (!showWelcome && companyInfo && state) {
      devModeService.setGameRefs({
        gameState: state,
        updateState,
        productActions: {
          products,
          progressProductToPhase,
          releaseProduct,
          discontinueProduct,
          updateProduct,
        },
        simulationActions: {
          changeSpeed,
          togglePause,
          setPaused,
          availableSpeeds: GAME_SPEEDS,
        },
        employeeActions: {
          hire: (role: string, count: number) => startHiring({
            role,
            count,
            gameDate,
            checkCapacity: checkCapacityForHiring,
            onEvent: addEvent
          }),
          fire: (role: string, count: number) => fireEmployees({
            role,
            count,
            onEvent: addEvent
          }),
          setEmployees,
          setHiringProcesses,
        }
      });
    }
  }, [showWelcome, companyInfo, state, products, employees, hiringProcesses,
    gameDate, checkCapacityForHiring, addEvent, startHiring, fireEmployees,
    setEmployees, setHiringProcesses]); // Updated dependencies

  // Game simulation loop
  useEffect(() => {
    // const timestamp = new Date().toISOString();
    // console.log(`[${timestamp}] [TycoonMode.gameSimulation] Effect running:`, {
    //   isPaused,
    //   showWelcome,
    //   hasCompanyInfo: !!companyInfo,
    //   isAnyModalOpen,
    //   willRun: !isPaused && !showWelcome && !!companyInfo && !isAnyModalOpen
    // });

    if (isPaused || showWelcome || !companyInfo || isAnyModalOpen) return;

    const interval = setInterval(async () => {
      // Advance game date using ref
      const currentDate = gameDateRef.current;
      const newDate = { ...currentDate };
      newDate.day += 1;

      if (newDate.day > 30) {
        newDate.day = 1;
        newDate.month += 1;

        if (newDate.month > 12) {
          newDate.month = 1;
          newDate.year += 1;
        }
      }

      // Sync competitor AI with current game date and execute daily actions
      try {
        const { competitorAI } = await import('../services/tycoon/competitorAIService');

        // Synchronize game date with competitor AI
        competitorAI.setCurrentGameDate(newDate);

        // Execute daily competitor actions (product launches, development progress, etc.)
        const competitorActions = await competitorAI.executeCompetitorActions(1000);
        if (competitorActions.length > 0) {
          console.log(`[TycoonMode] [${newDate.month}/${newDate.day}/${newDate.year}] 🤖 Executed ${competitorActions.length} competitor actions:`,
            competitorActions.map(a => `${a.competitorId}: ${a.action}${a.productName ? ` (${a.productName})` : ''}`));
        }

        // Simulate competitor sales for the day
        await competitorAI.simulateCompetitorSales(newDate);
      } catch (error) {
        console.error(`[TycoonMode] [${newDate.month}/${newDate.day}/${newDate.year}] ❌ Error executing competitor actions:`, error);
      }

      // Calculate daily revenue and costs
      let dailyRevenue = 0;
      let dailyCosts = 0;
      const salesBreakdowns: any[] = [];

      // console.log(`[${new Date().toISOString()}] [TycoonMode.gameLoop] Starting cost calculation for date ${newDate.month}/${newDate.day}/${newDate.year}`);

      // Calculate revenue from active products using ref
      if (activeProductsRef.current.length > 0) { 
        // console.log(`[TycoonMode] [${currentDate.month}/${currentDate.day}/${currentDate.year}] 📊 Starting sales calculations for ${activeProductsRef.current.length} active products`); 
      }

      // Use batch sales calculation for all active products
      const salesCalculationService = SalesCalculationService.getInstance();

      // Get company data for sales calculations
      const companyState = useCompanyStore.getState();
      const companyReputation = companyState.reputation || 0;
      const companyMarketShare = companyState.marketShare || 0.00;

      // Debug: Log reputation value in game tick
      // console.log(`[GameTick] Current reputation: ${companyReputation}, marketShare: ${companyMarketShare}`);

      // Get current inventory levels
      const inventoryStore = useInventoryStore.getState();
      const inventoryMap = new Map<string, number>();
      inventoryStore.devices.forEach((device, productId) => {
        inventoryMap.set(productId, device.currentStock);
      });

      // Convert newDate to Date object
      // const currentDate = new Date(newDate.year, newDate.month - 1, newDate.day);

      const salesResults = salesCalculationService.batchCalculateSales(
        activeProductsRef.current,
        currentDate,
        companyReputation,
        companyMarketShare,
        inventoryMap
      );

      // Process each product's sales
      for (const [productId, salesBreakdown] of salesResults) {
        const product = activeProductsRef.current.find(p => p.id === productId);
        if (!product) continue;

        // Additional safety checks before processing sales
        const currentInventory = inventoryMap.get(productId) || 0;
        
        // Verify all conditions are met for sales
        if (product.status !== 'released' ||
            currentInventory <= 0) {
          console.log(`[TycoonMode] ⚠️ Skipping sales for ${product.name}:`, {
            status: product.status,
            inventory: currentInventory,
            reason: product.status !== 'released' ? 'Not released' :
                   'No inventory'
          });
          continue;
        }

        const unitsSold = Math.floor(salesBreakdown.dailySales);
        const revenue = unitsSold * (product.price || 0);
        dailyRevenue += revenue;

        const wasInventoryConstrained = inventoryMap.get(productId) !== undefined &&
          unitsSold < salesBreakdown.dailySales;

        if (import.meta.env.DEV) {
          console.log(`[TycoonMode] Sales for ${product.name}:`, {
            unitsSold,
            revenue,
            inventoryConstrained: wasInventoryConstrained,
            baseDemand: salesBreakdown.baseDemand,
            priceMultiplier: salesBreakdown.priceMultiplier
          });
        }

        // Deduct sold units from device inventory
        if (unitsSold > 0) {
          try {
            await inventoryManagementService.updateDeviceStock(
              product.id,
              -unitsSold, // Negative quantity for sales
              false, // isProduction = false
              undefined // No unit value update
            );
            // console.log(`[TycoonMode] 📦 Deducted ${unitsSold} units from inventory for ${product.name}`);
          } catch (error) {
            console.error(`[TycoonMode] ❌ Failed to deduct inventory for ${product.name}:`, error);
          }
        }

        salesBreakdowns.push({
          productId: product.id,
          unitsSold,
          revenue
        });

        // Record supply/demand history for the chart
        const supplyDemandHistoryStore = useSupplyDemandHistoryStore.getState();
        supplyDemandHistoryStore.recordDataPoint(
          product.id,
          currentDate,
          currentInventory,                    // Supply (inventory before sales)
          Math.round(salesBreakdown.baseDemand), // Demand
          unitsSold                             // Actual sales
        );
      }
      if (activeProductsRef.current.length > 0) {
        // console.log(`[TycoonMode] [${currentDate.month}/${currentDate.day}/${currentDate.year}] 📊 Finished sales calculations for ${activeProductsRef.current.length} active products`);
      }

      // Calculate daily costs using refs
      // console.log(`[${new Date().toISOString()}] [TycoonMode.gameLoop] Calculating costs:`, {
      //   employeesCount: employeesRef.current?.length || 0,
      //   employees: employeesRef.current,
      //   buildingsCount: buildingsRef.current?.length || 0,
      //   buildings: buildingsRef.current
      // });

      // Check if refs are properly initialized
      if (!employeesRef.current) {
        console.error(`[${new Date().toISOString()}] [TycoonMode.gameLoop] employeesRef.current is null/undefined!`);
      }
      if (!buildingsRef.current) {
        console.error(`[${new Date().toISOString()}] [TycoonMode.gameLoop] buildingsRef.current is null/undefined!`);
      }

      const employeeCosts = employeesRef.current?.reduce((sum, emp) => {
        if (!emp || typeof emp.salary !== 'number') {
          console.error(`[${new Date().toISOString()}] [TycoonMode.gameLoop] Invalid employee:`, emp);
          return sum;
        }
        const dailySalary = emp.salary / 365;
        // console.log(`[${new Date().toISOString()}] [TycoonMode.gameLoop] Employee ${emp.id}: salary=${emp.salary}, daily=${dailySalary}`);
        return sum + dailySalary;
      }, 0) || 0;

      const buildingCosts = buildingsRef.current?.reduce((sum, building) => {
        if (!building || !building.buildingTypeId) {
          console.error(`[${new Date().toISOString()}] [TycoonMode.gameLoop] Invalid building:`, building);
          return sum;
        }

        // Get the building type definition to find maintenance cost
        const buildingType = BUILDINGS_CONSTANTS.types[building.buildingTypeId as keyof typeof BUILDINGS_CONSTANTS.types];
        if (!buildingType) {
          console.error(`[${new Date().toISOString()}] [TycoonMode.gameLoop] Building type not found: ${building.buildingTypeId}`);
          return sum;
        }

        const maintenanceCost = buildingType.maintenanceCost || 0;
        const dailyMaintenance = maintenanceCost / 30;
        // console.log(`[${new Date().toISOString()}] [TycoonMode.gameLoop] Building ${building.id} (${building.buildingTypeId}): maintenance=${maintenanceCost}, daily=${dailyMaintenance}`);
        return sum + dailyMaintenance;
      }, 0) || 0;

      dailyCosts = employeeCosts + buildingCosts;

      // console.log(`[${new Date().toISOString()}] [TycoonMode.gameLoop] Cost calculation results:`, {
      //   employeeCosts,
      //   buildingCosts,
      //   dailyCosts,
      //   dailyCostsType: typeof dailyCosts
      // });

      // Get current cash value for calculating new cash
      const currentCash = useCompanyStore.getState().finances.cash;
      const profit = dailyRevenue - dailyCosts;
      const newCash = currentCash + profit;

      // Calculate reputation changes
      const currentReputation = companyReputation || 0;

      // Apply reputation decay only when reputation is over 70
      let newReputation = currentReputation;
      if (currentReputation > 70) {
        newReputation = currentReputation * GAME_CONSTANTS.reputation.decayRate;
      }

      // Calculate reputation based on product performance (ratings)
      let ratingReputationChange = 0;
      activeProductsRef.current.forEach(product => {
        if (product.status === 'released') {
          const rating = product.salesMetrics?.averageRating || 0;

          if (rating >= 4.0) {
            // Successful product: gain reputation
            ratingReputationChange += GAME_CONSTANTS.reputation.gainPerSuccessfulProduct;
          } else if (rating < 3.0 && rating > 0) {
            // Poor product: lose reputation
            ratingReputationChange -= GAME_CONSTANTS.reputation.lossPerPoorProduct;
          }
          // Rating 3.0-3.9 or no rating: no change
        }
      });

      // NEW: Calculate reputation from sales and market share
      let totalDailySales = 0;
      salesBreakdowns.forEach(breakdown => {
        totalDailySales += breakdown.unitsSold;
      });

      // Calculate sales-based reputation gain using the new formula
      const salesReputationGain = GAME_FORMULAS.reputation.calculateReputationGrowthRate(
        currentReputation,
        totalDailySales,
        companyMarketShare
      );

      // Combine rating-based and sales-based reputation changes
      const totalReputationChange = ratingReputationChange + salesReputationGain;
      newReputation += totalReputationChange;

      // Clamp reputation between 0 and 100
      newReputation = Math.max(0, Math.min(100, newReputation));

      const activeProductCount = activeProductsRef.current.filter(p =>
        p.status === 'released'
      ).length;

      // console.log(`[GameTick] Reputation update: ${currentReputation} -> ${newReputation} (rating change: ${ratingReputationChange}, sales gain: ${salesReputationGain}, total sales: ${totalDailySales}, market share: ${companyMarketShare}%)`);
      // console.log(`[TycoonMode] Reputation updated and set in state: ${newReputation}`);

      // Record reputation history
      reputationHistoryService.recordReputation(currentDate, newReputation);

      // const currentDateStr = `${currentDate.year}/${currentDate.month}/${currentDate.day}`

      // Additional validation logging
      // console.log(`[${currentDateStr}] [TycoonMode.gameLoop] Financial calculation:`, {
      //   currentCash,
      //   dailyRevenue,
      //   dailyCosts,
      //   profit,
      //   newCash,
      //   profitCalculation: `${dailyRevenue} - ${dailyCosts} = ${profit}`,
      //   newCashCalculation: `${currentCash} + ${profit} = ${newCash}`
      // });

      // Log cash calculation details
      // const timestamp = new Date().toISOString();
      // console.log(`[${timestamp}] [TycoonMode.gameLoop] Daily tick:`, {
      //   currentDate: `${newDate.month}/${newDate.day}/${newDate.year}`,
      //   currentCash,
      //   dailyRevenue,
      //   dailyCosts,
      //   profit,
      //   newCash,
      //   willSetCashTo: newCash
      // });

      // if (currentCash === 100000000 && newCash !== currentCash) {
      //   console.warn(`[${timestamp}] [TycoonMode.gameLoop] First cash change from initial 100M detected!`);
      // }

      // Use batched update to prevent multiple renders
      gameTickUpdate({
        newDate,
        cash: newCash,
        dailyRevenue,
        dailyCosts,
        salesBreakdowns,
        reputation: newReputation
      });

      // Record monthly snapshot when transitioning to a new month
      // IMPORTANT: Must happen BEFORE accumulating today's data to avoid
      // including day 1's data in the previous month's snapshot
      if (newDate.day === 1) {
        // We just transitioned from previous month - record snapshot for the completed month
        const financialHistoryStore = useFinancialHistoryStore.getState();
        financialHistoryStore.recordMonthlySnapshot({
          date: { month: currentDate.month, year: currentDate.year },
          revenue: monthlyAccumulatorRef.current.revenue,
          costs: monthlyAccumulatorRef.current.costs,
          profit: monthlyAccumulatorRef.current.revenue - monthlyAccumulatorRef.current.costs,
          cash: newCash
        });

        // Reset accumulator for new month
        monthlyAccumulatorRef.current = { revenue: 0, costs: 0 };
      }

      // Accumulate daily stats for monthly financial history
      // This runs AFTER the snapshot check so day 1 data goes to the new month
      monthlyAccumulatorRef.current.revenue += dailyRevenue;
      monthlyAccumulatorRef.current.costs += dailyCosts;

      // Update product sales
      if (updateProductSalesRef.current && salesBreakdowns.length > 0) {
        updateProductSalesRef.current(salesBreakdowns);
      }

      // Calculate and sync market share for all products (player + competitors)
      // This ensures ProductCard shows the same market share as ProductListingTable
      try {
        const totalGameDays = (newDate.year - 2000) * 365 + (newDate.month - 1) * 30 + newDate.day;
        const marketAnalysis = await marketShareService.calculateMarketShares(totalGameDays);

        // Sync calculated market share to Zustand store for player products
        const productStoreState = useProductStore.getState();
        marketAnalysis.marketShares.forEach(({ productId, marketShare }) => {
          const product = productStoreState.products.find(p => p.id === productId);
          if (product && product.metadata?.companyId === 'player') {
            productStoreState.updateProduct(productId, {
              salesMetrics: {
                ...product.salesMetrics,
                marketShare: marketShare  // Decimal 0-1 format
              }
            });
          }
        });
      } catch (error) {
        console.error('[TycoonMode] Failed to calculate market share:', error);
      }

      /*
      // Log player action
      playerActionService.logDailyUpdate({
        revenue: dailyRevenue,
        costs: dailyCosts,
        profit,
        netWorth: newCash, // Using cash as net worth for now
        cash: newCash,
        date: newDate
      });
      */

    }, speed.tickRate);

    return () => clearInterval(interval);
  }, [isPaused, showWelcome, companyInfo, isAnyModalOpen, speed.tickRate]);
  // Remove state values and store actions from dependencies to prevent infinite loops

  // Update employees and hiring processes daily
  useEffect(() => {
    if (!isPaused && companyInfo && gameDate.day) {
      // Update employee metrics daily
      updateEmployeeMetrics();

      // Update hiring processes
      updateHiringProcesses({
        gameDate,
        onEvent: addEvent
      });

      // Note: Cleanup logic moved to separate effect below to prevent infinite loops
    }
  }, [gameDate.day, gameDate.month, gameDate.year, isPaused, companyInfo, updateEmployeeMetrics, updateHiringProcesses, addEvent]);

  // Clean up orphaned allocations immediately when products change (not just daily)
  useEffect(() => {
    if (!isPaused && companyInfo && products.length > 0 && employeeAllocations.length > 0) {
      const cleanedAllocations = employeeAllocationService.cleanupOrphanedAllocations(
        products,
        employeeAllocations
      );

      if (cleanedAllocations.length !== employeeAllocations.length) {
        console.log(`[TycoonMode] Cleaning up ${employeeAllocations.length - cleanedAllocations.length} orphaned allocation(s) due to product status changes`);
        setEmployeeAllocations(cleanedAllocations);
      }
    }
  }, [products, isPaused, companyInfo, employeeAllocations, setEmployeeAllocations]);

  // Retry employee allocation for products that are waiting (at 100% progress but not complete)
  useEffect(() => {
    if (!isPaused && companyInfo && products.length > 0) {
      products.forEach(product => {
        // Check if product is at 100% progress but not complete (waiting for employees)
        if (product.developmentPhase && product.status !== 'production' && product.status !== 'discontinued') {
          const daysSince = calculateDaysSince(product.developmentPhase.startDate, gameDate);
          const progress = Math.min(100, (daysSince / product.developmentPhase.estimatedDuration) * 100);

          // If at 100% progress but not complete, try to allocate employees
          if (progress >= 100 && !product.developmentPhase.isComplete) {
            // Check if event was already triggered (waiting for modal choice)
            // Don't retry if we're waiting for user to respond to the phase modal
            const eventKey = `${product.id}-${product.status}`;
            if (triggeredPhaseEventsRef.current.has(eventKey)) {
              // Event already triggered, waiting for user modal choice - don't retry
              return;
            }

            const nextPhaseMap: Record<string, Product['status']> = {
              'design': 'engineering',
              'engineering': 'testing',
              'testing': 'manufacturing',
              'manufacturing': 'production'
            };

            const nextStatus = nextPhaseMap[product.status];
            if (nextStatus && nextStatus !== 'production') {
              const techScore = product.techScore || 0;
              const employeeCheck = employeeAllocationService.canAllocateEmployees(
                nextStatus as DevelopmentPhase,
                techScore,
                gameDate.year,
                employees,
                employeeAllocations
              );

              // If we can now allocate, trigger progression
              if (employeeCheck.canAllocate) {
                console.log(`[TycoonMode] Retrying progression for "${product.name}" - employees now available`);
                // Trigger the progression logic by calling progressPhases
                progressPhases(gameDate);
                return; // Exit loop after first successful retry to avoid over-processing
              }
            }
          }
        }
      });
    }
  }, [employees, employeeAllocations, gameDate.day, isPaused, companyInfo, products, progressPhases]);

  // Process dynamic events when game state changes
  useEffect(() => {
    if (!isPaused && companyInfo) {
      // Calculate month since start of game (January 2024 = month 1)
      const currentMonth = (gameDate.year - GAME_CONFIG.STARTING_YEAR) * 12 + gameDate.month;
      // Create state object inside effect to avoid dependency issues
      const currentState = {
        cash,
        reputation,
        marketShare,
        researchPoints,
        day: gameDate.day,
        month: gameDate.month,
        year: gameDate.year,
        isGameOver,
        bankruptcyWarnings,
        dailyStats,
        marketData,
        research,
        employees,
        hiringProcesses,
        buildings,
        employeeAllocations
      };
      if (eventsActions && eventsActions.processEvents) {
        eventsActions.processEvents(currentState, currentMonth);
      }
    }
  }, [gameDate.month, gameDate.year, isPaused, companyInfo, eventsActions]); // Only depend on date changes

  // Progress product development phases based on game date
  useEffect(() => {
    if (!isPaused && companyInfo) {
      progressPhases(gameDate);
    }
  }, [gameDate.day, gameDate.month, gameDate.year, isPaused, companyInfo, progressPhases]);

  // Process manufacturing and update inventory daily
  useEffect(() => {
    if (!isPaused && companyInfo && gameDate.day && gameDate.month && gameDate.year) {
      const processManufacturing = async () => {
        try {
          // console.log(`[TycoonMode] 🏭 Processing manufacturing for ${gameDate.month}/${gameDate.day}/${gameDate.year}`, {
          //   productCount: products.length,
          //   employeeCount: employees.length,
          //   buildingCount: buildings.length,
          //   activeProductsCount: activeProductsRef.current.length
          // });

          // Initialize production lines if needed
          factoryProductionService.initializeProductionLines(buildings, products);

          // Log product status for debugging

          // Process factory production
          const productionResult = await factoryProductionService.processDailyProduction(
            products,
            employees,
            buildings,
            gameDate
          );

          // console.log('[TycoonMode] 📊 Production result:', {
          //   manufacturingStarted: productionResult.manufacturingStarted.length,
          //   constraints: productionResult.constraints.length,
          //   linesResumed: productionResult.linesResumed.length,
          //   productionCapacityProducts: productionResult.productionCapacity.size
          // });

          if (productionResult.manufacturingStarted.length > 0) {
            // console.log('[TycoonMode] 📦 Manufacturing started for:', productionResult.manufacturingStarted);
          }
          
          if (productionResult.constraints.length > 0) {
            // console.log('[TycoonMode] ⚠️ Production constraints:', productionResult.constraints);
          }

          // Create events for resumed production lines
          if (productionResult.linesResumed && productionResult.linesResumed.length > 0) {
            productionResult.linesResumed.forEach(resumed => {
              const product = products.find(p => p.id === resumed.productId);
              if (product) {
                addEvent({
                  type: 'production' as any,
                  title: 'Production Line Resumed',
                  description: `Production line resumed for ${product.name} - components now available`,
                  impact: 'positive'
                });
              }
            });
          }

          // Process ongoing manufacturing
          const manufacturingResult = await manufacturingService.processDailyManufacturing(
            gameDate,
            products,
            employees
          );

          // console.log('[TycoonMode] 🏭 Manufacturing processing result:', {
          //   completedBatches: manufacturingResult.completedBatches.length,
          //   updatedWIP: manufacturingResult.updatedWIP.length
          // });

          if (manufacturingResult.completedBatches.length > 0) {
            // console.log('[TycoonMode] ✅ Production completed:', manufacturingResult.completedBatches);

            // Create events for completed production
            manufacturingResult.completedBatches.forEach(batch => {
              const product = products.find(p => p.id === batch.productId);
              if (product) {
                // console.log('[TycoonMode] 📦 Creating completion event for:', {
                //   productId: batch.productId,
                //   productName: product.name,
                //   quantity: batch.quantity
                // });
                
                addEvent({
                  type: 'production' as any,
                  title: 'Production Complete',
                  description: `Finished manufacturing ${batch.quantity.toLocaleString()} units of ${product.name}`,
                  impact: 'positive'
                });
              }
            });
          }

          // Process component orders
          const deliveredOrders = await inventoryManagementService.processPendingOrders(gameDate);

          if (deliveredOrders.length > 0) {
            // console.log('📦 Component orders delivered:', deliveredOrders);

            // Import inventory store to update order status only
            const { useInventoryStore } = await import('../stores/inventoryStore');
            const inventoryStore = useInventoryStore.getState();

            // Create events for delivered orders using event store
            // const { eventStore } = await import('../services/tycoon/eventStore');
            for (const order of deliveredOrders) {
              // Add event to event store so it appears in Event Log
              addEvent({
                id: `delivery-${order.orderId}`,
                type: 'component',
                title: 'Component Order Delivered',
                description: `Received ${order.quantity.toLocaleString()} units of ${order.componentName}`,
                impact: 'neutral'
              });

              // IMPORTANT: Do NOT update inventory quantities here!
              // inventoryManagementService.processPendingOrders() has already updated the inventory
              // We only need to update the order status in the Zustand store

              // Mark the order as delivered in the store
              const storeOrder = inventoryStore.purchaseOrders.find(o => o.id === order.orderId);
              if (storeOrder) {
                inventoryStore.updatePurchaseOrder(order.orderId, {
                  status: 'delivered',
                  actualDelivery: gameDate
                });
              }
            }
          }

          // Process auto-orders (zero inventory and recurring)
          try {
            const currentInventoryStore = useInventoryStore.getState();
            // Get fresh cash value to avoid stale closure reads
            const freshCash = useCompanyStore.getState().finances.cash;
            const autoOrderResult = await autoOrderService.processAllAutoOrders(
              gameDate,
              currentInventoryStore.components,
              currentInventoryStore.autoOrderSettings,
              freshCash
            );

            if (autoOrderResult.orders.length > 0) {
              // Deduct the total cost with negative cash protection
              if (autoOrderResult.totalCost > 0) {
                const currentCash = useCompanyStore.getState().finances.cash;
                if (currentCash >= autoOrderResult.totalCost) {
                  setCash(currentCash - autoOrderResult.totalCost);
                } else {
                  addEvent({
                    type: 'warning',
                    title: 'Auto-Order Failed',
                    description: 'Auto-orders skipped due to insufficient funds',
                    impact: 'negative'
                  });
                }
              }

              // Update auto-order settings with lastOrderDate
              for (const [componentId, setting] of autoOrderResult.updatedSettings) {
                currentInventoryStore.setAutoOrderSetting(componentId, setting);
              }

              // Fire events for auto-orders
              for (const order of autoOrderResult.orders) {
                addEvent({
                  type: 'production',
                  title: 'Auto-Order Placed',
                  description: `Automatically ordered ${order.quantity.toLocaleString()} units of ${order.componentName}`,
                  impact: 'neutral'
                });
              }
            }
          } catch (autoOrderError) {
            console.error('Failed to process auto-orders:', autoOrderError);
          }

        } catch (error) {
          console.error('Failed to process manufacturing:', error);
        }
      };

      processManufacturing();
    }
  }, [gameDate.day, gameDate.month, gameDate.year, isPaused, companyInfo, products,
    employees, buildings]);

  // Set up event engine preferences and product progression callback
  useEffect(() => {
    const setupEventEngine = () => {
      eventEngine.setPhasePreferences(phasePreferences, phasePreferencesLastChanged || undefined);

      // Set up product progression callback for auto-handled events
      // This callback is invoked when events are auto-handled based on user preferences
      eventEngine.setProductProgressionCallback((productId: string, targetPhase: string, callbackGameDate: { day: number; month: number; year: number }, investment?: any) => {
        const effectiveGameDate = getEffectiveGameDate(callbackGameDate, gameDateRef.current);
        const latestProducts = useProductStore.getState().products;
        const product = latestProducts.find(p => p.id === productId) || products.find(p => p.id === productId);

        if (targetPhase === 'investment' && investment) {
          // Apply investments to the product
          applyPhaseInvestment(productId, investment, effectiveGameDate);
          return;
        }

        // Progress to the next phase using full advancement logic
        if (!product) {
          console.warn(`[TycoonMode] Product ${productId} not found for phase advancement`);
          return;
        }

        const result = advanceProductToNextPhase(
          product,
          targetPhase as ProductType['status'],
          effectiveGameDate as typeof gameDate,
          [] // No pending allocations in auto-handle context
        );

        if (!result.success) {
          // Show warning event for insufficient employees
          console.warn(`[TycoonMode] Auto-advance failed for ${product.name}: ${result.reason}`);
          addEvent({
            id: uuidv4(),
            type: 'employee_shortage',
            title: 'Employee Shortage',
            message: `Product "${product.name}" cannot advance: ${result.reason}`,
            date: effectiveGameDate,
            severity: 'warning'
          });
        }
      });
    };

    if (companyInfo) {
      setupEventEngine();
    }
  }, [phasePreferences, phasePreferencesLastChanged, companyInfo, products, advanceProductToNextPhase, applyPhaseInvestment, addEvent]);

  // Reset event engine when starting a new game
  useEffect(() => {
    if (companyInfo && !showWelcome && eventsActions && eventsActions.resetEventEngine) {
      eventsActions.resetEventEngine();
    }
  }, [companyInfo, showWelcome, eventsActions]); // Remove eventsActions from deps

  // Bankruptcy warning check
  useEffect(() => {
    if (cash < 0 && !showBankruptcyWarning && !isGameOver) {
      setShowBankruptcyWarning(true);
      pauseGame();
    }
  }, [cash, isGameOver, showBankruptcyWarning, pauseGame]);

  // Pause game when reactive events are pending (phase completion modals)
  // This prevents multiple phases from completing while user is making a decision
  useEffect(() => {
    const hasPendingEvents = (eventsData.pendingReactiveEvents?.length || 0) > 0;

    if (hasPendingEvents && !isPaused) {
      // Pause the game when reactive event modal appears
      pausedForReactiveEventRef.current = true;
      pauseGame();
    } else if (!hasPendingEvents && pausedForReactiveEventRef.current) {
      // Resume the game only if we were the ones who paused it
      pausedForReactiveEventRef.current = false;
      resumeGame();
    }
  }, [eventsData.pendingReactiveEvents?.length, isPaused, pauseGame, resumeGame]);

  // Auto-dismiss stale phase events after the product has already moved on
  useEffect(() => {
    const pendingEvent = eventsData.pendingReactiveEvents?.[0];
    if (!pendingEvent?.productContext) {
      return;
    }

    const product = products.find(p => p.id === pendingEvent.productContext?.productId);
    if (!isStalePhaseEvent(pendingEvent, product)) {
      return;
    }

    console.warn('[TycoonMode] Auto-dismissing stale phase event', {
      eventId: pendingEvent.event.id,
      productId: pendingEvent.productContext.productId,
      phase: pendingEvent.productContext.phase,
      status: product?.status
    });

    const triggerKey = `${pendingEvent.productContext.productId}-${pendingEvent.productContext.phase}`;
    triggeredPhaseEventsRef.current.delete(triggerKey);

    const shouldInferProceed = product?.status && product.status !== pendingEvent.productContext.phase;
    const resolvedChoiceId = pendingEvent.playerChoice || (shouldInferProceed ? 'proceed_to_next_stage' : undefined);

    if (resolvedChoiceId) {
      resolveReactiveEventChoice(pendingEvent, resolvedChoiceId);
      return;
    }

    eventService.handlePlayerChoice(pendingEvent.event.id);
  }, [eventsData.pendingReactiveEvents, products, resolveReactiveEventChoice]);

  const handlePhasePreferenceChange = useCallback((phase: keyof PhaseProgressionPreferences, choice: string) => {
    const newPreferences = { ...phasePreferences, [phase]: choice };
    const changeTime = new Date();

    setPhasePreferences(newPreferences);
    setPhasePreferencesLastChanged(changeTime);

    // Save to localStorage
    const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
    const prefs = JSON.parse(savedPrefs);
    prefs.phasePreferences = newPreferences;
    localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));

    // Immediately sync with event engine to avoid race conditions
    if (companyInfo) {
      import('../services/tycoon/eventEngine').then(({ eventEngine }) => {
        eventEngine.setPhasePreferences(newPreferences, changeTime);
      });
    }
  }, [phasePreferences, companyInfo]);

  // Enhanced event choice handler that integrates with product pipeline
  const handlePhaseEventChoice = useCallback(async (eventId: string, choiceId: string, dontAskAgain?: boolean) => {
    let isInvestmentChoice = false;
    let didHandleChoice = false;
    let pendingEvent: ActiveEvent | undefined;

    try {
      pendingEvent = eventsData.pendingReactiveEvents?.find(e => e.event.id === eventId);
      if (!pendingEvent) {
        return;
      }

      // Guard against multi-clicks - prevent processing same event twice
      const eventKey = `${eventId}-${pendingEvent.productContext?.productId ?? 'global'}`;
      if (processingEventRef.current === eventKey) {
        console.log('[TycoonMode] Already processing event choice, skipping duplicate click');
        return;
      }
      processingEventRef.current = eventKey;

      if (pendingEvent.productContext) {
        const product = products.find(p => p.id === pendingEvent.productContext?.productId);
        if (isStalePhaseEvent(pendingEvent, product)) {
          console.warn('[TycoonMode] Dismissing stale phase event', {
            eventId,
            productId: pendingEvent.productContext.productId,
            phase: pendingEvent.productContext.phase,
            status: product?.status
          });

          const triggerKey = `${pendingEvent.productContext.productId}-${pendingEvent.productContext.phase}`;
          triggeredPhaseEventsRef.current.delete(triggerKey);

          resolveReactiveEventChoice(pendingEvent, choiceId);
          didHandleChoice = true;
          return;
        }
      }

      const choice = pendingEvent.event.choices?.find(c => c.id === choiceId);
      if (!choice) {
        processingEventRef.current = null;
        return;
      }

      isInvestmentChoice = choiceId === 'minor_investment' || choiceId === 'major_investment';

      // If "don't ask again" is checked, save the preference
      if (dontAskAgain && pendingEvent.event.id.includes('_complete') && pendingEvent.productContext) {
        const phaseKey = pendingEvent.event.id as keyof PhaseProgressionPreferences;
        const { productId, phase } = pendingEvent.productContext;
        let preferenceValue = 'ask';

        // Map standardized choice IDs to preference values
        if (choiceId === 'proceed_to_next_stage') {
          preferenceValue = 'proceed';
        } else if (choiceId === 'minor_investment') {
          preferenceValue = 'minor';
        } else if (choiceId === 'major_investment') {
          preferenceValue = 'major';
        }

        // Update global preference - that's all we need to do!
        handlePhasePreferenceChange(phaseKey, preferenceValue);
      }

      // Check cost requirements
      if (choice.costRequirement?.cash && cash < choice.costRequirement.cash) {
        setError(`Insufficient funds. Required: ${formatCurrency(choice.costRequirement.cash)}`);
        processingEventRef.current = null;
        return;
      }

      // Apply cash cost
      if (choice.costRequirement?.cash) {
        setCash(cash - choice.costRequirement.cash);
      }

      // Apply immediate effects to product
      if (choice.outcome.immediateEffects && pendingEvent.productContext) {
        const { productId } = pendingEvent.productContext;

        // Apply phase progression with full employee allocation logic
        if (choice.outcome.immediateEffects.phaseProgression?.targetPhase) {
          const product = products.find(p => p.id === productId);
          if (product) {
            const targetPhase = choice.outcome.immediateEffects.phaseProgression.targetPhase;
            const result = advanceProductToNextPhase(
              product,
              targetPhase as ProductType['status'],
              gameDate,
              [] // No pending allocations in modal context
            );

            if (!result.success) {
              // Show error and don't dismiss the event - let user try again later
              setError(`Cannot advance to ${targetPhase}: ${result.reason}`);
              processingEventRef.current = null;
              return; // Don't dismiss the event
            }
          }
        }

        // Apply investment effects (for minor/major investment choices)
        const investment: any = {};
        if (choice.outcome.immediateEffects.developmentPhase) {
          investment.additionalDays = choice.outcome.immediateEffects.developmentPhase.additionalDays;
          investment.confidence = choice.outcome.immediateEffects.developmentPhase.confidence;
          investment.qualityScore = choice.outcome.immediateEffects.developmentPhase.qualityScore;
        }
        if (choice.outcome.immediateEffects.techScore) {
          investment.techScore = choice.outcome.immediateEffects.techScore;
        }
        if (choice.outcome.immediateEffects.marketAppeal) {
          investment.marketAppeal = choice.outcome.immediateEffects.marketAppeal;
        }
        if (choice.outcome.immediateEffects.productionCostReduction) {
          investment.productionCostReduction = choice.outcome.immediateEffects.productionCostReduction;
        }

        if (Object.keys(investment).length > 0) {
          applyPhaseInvestment(productId, investment, gameDate);
        }
      }

      // Apply other state effects
      if (choice.outcome.immediateEffects) {
        const effects = choice.outcome.immediateEffects;

        if (effects.cash) setCash(cash + effects.cash);
        if (effects.reputation || effects.marketShare) {
          console.log(`[ReactiveEvent] Updating reputation: ${reputation} + ${effects.reputation || 0} = ${reputation + (effects.reputation || 0)}`);
          updateMetrics({
            ...(effects.reputation && { reputation: reputation + effects.reputation }),
            ...(effects.marketShare && { marketShare: marketShare + effects.marketShare })
          });
        }
        if (effects.researchPoints) {
          setResearchPoints(researchPoints + effects.researchPoints);
        }
      }

      // Clear triggered event ref once the choice is handled so we can re-prompt after investment
      if (pendingEvent.productContext && (choiceId === 'proceed_to_next_stage' || isInvestmentChoice)) {
        const eventKey = `${pendingEvent.productContext.productId}-${pendingEvent.productContext.phase}`;
        triggeredPhaseEventsRef.current.delete(eventKey);
      }

      // Handle the event choice through the normal system
      resolveReactiveEventChoice(pendingEvent, choiceId);
      didHandleChoice = true;
    } finally {
      if (isInvestmentChoice && !didHandleChoice && pendingEvent) {
        resolveReactiveEventChoice(pendingEvent, choiceId);
        didHandleChoice = true;
      }

      if (!didHandleChoice) {
        processingEventRef.current = null;
      }
    }
  }, [eventsData.pendingReactiveEvents, cash, setCash, updateMetrics, reputation, marketShare,
    researchPoints, setResearchPoints, products, advanceProductToNextPhase, applyPhaseInvestment,
    resolveReactiveEventChoice, handlePhasePreferenceChange, gameDate, setError]);

  const handleRestartTutorial = useCallback(() => {
    // Close settings modal first
    setShowSettings(false);
    // Pause the game
    pauseGame();
    // Reset and restart tutorial
    // The TutorialProvider will detect the state change and show the welcome
    resetTutorial();
  }, [resetTutorial, pauseGame]);

  const handleSaveGame = async () => {
    if (!companyInfo) return;

    setIsSaving(true);
    setSaveSuccess(false);
    setError(null);

    // Fetch production batches from the database
    let productionBatches = [];
    try {
      const { ProductionService } = await import('../services/ProductionService');
      const allBatches = await ProductionService.getAllBatches();
      productionBatches = allBatches.map(batch => ({
        ...batch,
        startDate: new Date(batch.startDate)
      }));
    } catch (error) {
      console.warn('Failed to fetch production batches for save:', error);
    }

    // Fetch competitor products from the database
    let competitorProducts = [];
    try {
      const { ProductManager } = await import('../services/productManager');
      const result = await ProductManager.getAllProducts();
      if (result.success && result.data) {
        competitorProducts = result.data.filter(product =>
          product.metadata?.companyId &&
          product.metadata.companyId !== 'player'
        );
        // console.log(`💾 Saving ${competitorProducts.length} competitor products`);
      }
    } catch (error) {
      // console.warn('Failed to fetch competitor products for save:', error);
    }

    // Fetch inventory state from the database
    let inventoryState = null;
    try {
      const { DatabaseService } = await import('../services/DatabaseService');

      // Get component inventory
      const componentInventory = await DatabaseService.getAll('component_inventory');
      const components = componentInventory.map(inv => ({
        id: inv.id,
        componentId: inv.componentId,
        componentName: inv.componentName,
        category: inv.category,
        currentStock: inv.currentStock,
        minStock: inv.minStock,
        maxStock: inv.maxStock,
        unitCost: inv.unitCost,
        consumptionRate: inv.consumptionRate,
        lastOrderDate: inv.lastOrderDate,
        lastOrderQuantity: inv.lastOrderQuantity,
        leadTimeDays: inv.leadTimeDays
      }));

      // Get device inventory
      const deviceInventory = await DatabaseService.getAll('device_inventory');
      const devices = deviceInventory.map(inv => ({
        id: inv.id,
        productId: inv.productId,
        productName: inv.productName,
        currentStock: inv.currentStock,
        productionRate: inv.productionRate,
        targetStock: inv.targetStock,
        qualityScore: inv.qualityScore
      }));

      // Get pending orders
      const componentOrders = await DatabaseService.getAll('component_orders');
      const pendingOrders = componentOrders.filter(order => order.status === 'pending').map(order => ({
        orderId: order.orderId,
        componentId: order.componentId,
        componentName: order.componentName,
        quantity: order.quantity,
        unitCost: order.unitCost,
        totalCost: order.totalCost,
        orderDate: new Date(order.orderDate),
        expectedDeliveryDate: new Date(order.expectedDeliveryDate),
        deliveryDate: order.deliveryDate ? new Date(order.deliveryDate) : undefined,
        status: order.status,
        gameOrderDate: order.gameOrderDate,
        gameDeliveryDate: order.gameDeliveryDate
      }));

      // Get autoOrderSettings from the inventory store and convert Map to object
      const inventoryStoreState = useInventoryStore.getState();
      const autoOrderSettingsObj: Record<string, any> = {};
      inventoryStoreState.autoOrderSettings.forEach((setting, componentId) => {
        autoOrderSettingsObj[componentId] = setting;
      });

      inventoryState = {
        components,
        devices,
        pendingOrders,
        autoOrderSettings: autoOrderSettingsObj
      };

      // console.log(`💾 Saving inventory state: ${components.length} components, ${devices.length} devices, ${pendingOrders.length} pending orders`);
    } catch (error) {
      // console.warn('Failed to fetch inventory state for save:', error);
    }

    const saveData = {
      version: '1.1.0',
      saveDate: new Date(),
      gameDate: {
        day: gameDate.day,
        month: gameDate.month,
        year: gameDate.year,
        hour: new Date().getHours(),
        minute: new Date().getMinutes()
      },
      company: {
        id: 'player',
        name: companyInfo.companyName,
        ceoName: companyInfo.ceoName,
        founded: {
          day: 1,
          month: 1,
          year: gameDate.year
        },
        metrics: {
          cash: isNaN(cash) ? 0 : cash,
          reputation: isNaN(reputation) ? 50 : reputation,
          marketShare: isNaN(marketShare) ? 0 : marketShare,
          researchPoints: isNaN(researchPoints) ? 0 : researchPoints,
          bankruptcyWarnings: bankruptcyWarnings || 0,
          isGameOver: isGameOver || false
        },
        finances: (useCompanyStore.getState().finances),
        research: currentResearchState || research,
        products: products,
        marketingCampaigns: [],
        factories: [],
        employees: employees,
        hiringProcesses: hiringProcesses,
        buildings: buildings || [],
        employeeAllocations: employeeAllocations || []
      },
      marketState: marketData ? {
        totalMarketSize: marketData.totalMarketSize || 500000000,
        marketGrowthRate: marketData.marketGrowthRate || 0.08,
        economicPhase: marketData.economicPhase || 'expansion',
        competitorActions: marketData.competitorActions || [],
        technologicalDisruptions: marketData.technologicalDisruptions || [],
        marketEvents: marketData.marketEvents || [],
        playerActions: marketData.playerActions || []
      } : {
        totalMarketSize: 500000000,
        marketGrowthRate: 0.08,
        economicPhase: 'expansion',
        competitorActions: [],
        technologicalDisruptions: [],
        marketEvents: [],
        playerActions: []
      },
      eventState: {
        eventHistory: eventsData.eventHistory || [],
        pendingReactiveEvents: eventsData.pendingReactiveEvents || [],
        systemicNotifications: eventsData.systemicNotifications || [],
        marketConditions: eventsData.currentMarketConditions || {}
      },
      productionBatches: productionBatches,
      productionLines: Array.from(useProductionStore.getState().productionLines.values()),
      competitorProducts: competitorProducts,
      dailyStats: {
        revenue: dailyStats?.revenue || 0,
        costs: dailyStats?.costs || 0,
        profit: dailyStats?.profit || 0,
        salesBreakdowns: dailyStats?.productSales?.map((sale: any) => ({
          productId: sale.productId,
          unitsSold: sale.unitsSold,
          revenue: sale.revenue
        })) || []
      },
      inventoryState: inventoryState,
      spatialState: {
        currentLocation: useSpatialStore.getState().currentLocation,
        unlockedLocations: useSpatialStore.getState().unlockedLocations,
        transitionPreferences: useSpatialStore.getState().transitionPreferences,
      },
      supplyDemandHistory: useSupplyDemandHistoryStore.getState().exportForSave(),
      financialHistory: useFinancialHistoryStore.getState().exportForSave(),
      currentMonthAccumulator: monthlyAccumulatorRef.current,
      settings: {
        gameSpeed: simulationSpeed,
        autosaveEnabled: false,
        autosaveInterval: 5,
        phaseProgressionPreferences: phasePreferences,
        fireWarningEnabled: showFireWarning,
        audio: audioSettings,
        tutorialCompleted: useUIStore.getState().tutorialCompleted,
        tutorialSkipped: useUIStore.getState().tutorialSkipped
      },
      statistics: {
        totalRevenue: isNaN(dailyStats?.revenue) ? 0 : (dailyStats?.revenue || 0),
        totalProfit: isNaN(dailyStats?.profit) ? 0 : (dailyStats?.profit || 0),
        productsLaunched: products.length || 0,
        marketSharePeak: isNaN(marketShare) ? 0 : marketShare,
        bankruptcies: 0,
        playTime: 0
      }
    };

    try {
      const result = await saveGame(saveData, currentSlotId);
      if (!currentSlotId) {
        setCurrentSlotId(result.slotId);
      }
      setSaveSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save game');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveSuccess(false), 2000);
    }
  };

  const handleStartGame = async (info: { ceoName: string; companyName: string }) => {
    try {
      const timestamp = new Date().toISOString();
      // console.log(`[${timestamp}] [TycoonMode.handleStartGame] 🚀 Starting new game...`);

      // Log current cash state before any operations
      const currentCash = useCompanyStore.getState().finances?.cash;
      // console.log(`[${timestamp}] [TycoonMode.handleStartGame] Current cash before reset: ${currentCash}`);

      // Step 1: Pause all store sync operations to prevent race conditions
      // console.log(`[${timestamp}] [TycoonMode.handleStartGame] ⏸️ Pausing store sync operations...`);
      const companyStoreSync = useCompanyStore.getState().sync;
      const gameStoreSync = useGameStore.getState().sync;
      const productStoreSync = useProductStore.getState().sync;
      const marketStoreSync = useMarketStore.getState().sync;
      const buildingStoreSync = useBuildingStore.getState().sync;

      // Pause all sync operations
      if (companyStoreSync?.pauseSync) companyStoreSync.pauseSync();
      if (gameStoreSync?.pauseSync) gameStoreSync.pauseSync();
      if (productStoreSync?.pauseSync) productStoreSync.pauseSync();
      if (marketStoreSync?.pauseSync) marketStoreSync.pauseSync();
      if (buildingStoreSync?.pauseSync) buildingStoreSync.pauseSync();

      // Step 2: Reset all game state (clears persistence)
      const timestamp2 = new Date().toISOString();
      // console.log(`[${timestamp2}] [TycoonMode.handleStartGame] 🔄 Resetting all game state...`);
      const { GameStateResetManager } = await import('../services/tycoon/gameStateResetManager');
      await GameStateResetManager.resetAllGameState();

      // Check cash after persistence clear
      const cashAfterClear = useCompanyStore.getState().finances?.cash;
      // console.log(`[${new Date().toISOString()}] [TycoonMode.handleStartGame] Cash after persistence clear: ${cashAfterClear}`);

      // Step 3: Reset company store to initial state (includes 100M cash)
      const timestamp3 = new Date().toISOString();
      // console.log(`[${timestamp3}] [TycoonMode.handleStartGame] 🏢 Resetting company store...`);
      resetCompany();

      // Check cash after resetCompany
      const cashAfterReset = useCompanyStore.getState().finances?.cash;
      // console.log(`[${new Date().toISOString()}] [TycoonMode.handleStartGame] Cash after resetCompany: ${cashAfterReset}`);

      // Step 4: Initialize game services
      const timestamp4 = new Date().toISOString();
      // console.log(`[${timestamp4}] [TycoonMode.handleStartGame] Initializing game services...`);
      await GameStateResetManager.initializeForNewGame({
        day: GAME_CONFIG.STARTING_DAY,
        month: GAME_CONFIG.STARTING_MONTH,
        year: GAME_CONFIG.STARTING_YEAR
      });
      // console.log(`[${new Date().toISOString()}] [TycoonMode.handleStartGame] ✅ Game state reset complete`);

      // Step 5: Set game state
      setCompanyInfo(info);
      setCurrentSlotId(null);
      setShowWelcome(false);
      setShowSettings(false);

      // Ensure game starts paused
      pauseGame();

      // Clear reputation history for new game
      reputationHistoryService.clearHistory();

      // Clear supply/demand history for new game
      useSupplyDemandHistoryStore.getState().reset();

      // Clear financial history for new game
      useFinancialHistoryStore.getState().reset();
      monthlyAccumulatorRef.current = { revenue: 0, costs: 0 };

      // Record initial snapshot for month 1 with starting cash
      // This ensures the chart shows month 1 data even before reaching month 2
      useFinancialHistoryStore.getState().recordMonthlySnapshot({
        date: { month: GAME_CONFIG.STARTING_MONTH, year: GAME_CONFIG.STARTING_YEAR },
        revenue: 0,
        costs: 0,
        profit: 0,
        cash: GAME_CONSTANTS.finance.initialCash
      });

      // Update company details (cash is already set by resetCompany)
      const timestamp5 = new Date().toISOString();
      // console.log(`[${timestamp5}] [TycoonMode.handleStartGame] Updating company details...`);
      updateCompany({
        id: 'player',
        name: info.companyName,
        ceoName: info.ceoName,
        founded: { day: GAME_CONFIG.STARTING_DAY, month: GAME_CONFIG.STARTING_MONTH, year: GAME_CONFIG.STARTING_YEAR },
        productCount: 0,
        isPlayer: true
      });

      // Clear player products for fresh start
      setProducts([]);
      setProducts([]);
      // console.log('✅ Player products cleared');

      // Initialize with starting building for new game
      // console.log(`[${new Date().toISOString()}] [TycoonMode.handleStartGame] About to create starting building...`);
      // console.log(`[${new Date().toISOString()}] [TycoonMode.handleStartGame] BUILDINGS_CONSTANTS.startingBuilding:`, BUILDINGS_CONSTANTS.startingBuilding);

      const startingBuilding = buildingService.purchaseBuilding(
        BUILDINGS_CONSTANTS.startingBuilding,
        { day: GAME_CONFIG.STARTING_DAY, month: GAME_CONFIG.STARTING_MONTH, year: GAME_CONFIG.STARTING_YEAR }
      );

      // console.log(`[${new Date().toISOString()}] [TycoonMode.handleStartGame] Starting building created:`, startingBuilding);
      setBuildings([startingBuilding]);
      // console.log(`[${new Date().toISOString()}] [TycoonMode.handleStartGame] ✅ Starting building added to state`);

      // Clear employees and hiring processes for fresh start
      setEmployees([]);
      setHiringProcesses([]);

      // Reset tutorial state for new game (allows tutorial to show again)
      useUIStore.getState().setTutorialCompleted(false);
      useUIStore.getState().setTutorialSkipped(false);
      useUIStore.getState().setTutorialStep(null);

      // Note: Cash is already set to initial value by resetCompany()

      // Step 6: Resume sync operations now that everything is initialized
      if (companyStoreSync?.resumeSync) companyStoreSync.resumeSync();
      if (gameStoreSync?.resumeSync) gameStoreSync.resumeSync();
      if (productStoreSync?.resumeSync) productStoreSync.resumeSync();
      if (marketStoreSync?.resumeSync) marketStoreSync.resumeSync();
      if (buildingStoreSync?.resumeSync) buildingStoreSync.resumeSync();

      // Game starts paused - do not resume here
      // Log company founding to new event system
      playerActionService.logCompanyFounding(info.companyName, info.ceoName);

      // Initialize competitor starting products
      // console.log('🚀 Initializing competitor AI...');
      const { competitorAI } = await import('../services/tycoon/competitorAIService');
      await competitorAI.initializeStartingProducts();
      // console.log('✅ Competitor starting products initialized successfully');
    } catch (error) {
      // console.error('❌ Failed to initialize fresh game:', error);
      setError('Failed to start new game. Please try again.');
    }
  };

  const handleLoadGame = async (save: GameSave, slotId: number) => {
    try {
      // Reset all game state before loading to prevent pollution
      const { GameStateResetManager } = await import('../services/tycoon/gameStateResetManager');
      await GameStateResetManager.prepareForSaveLoad(save.gameDate);
      // console.log('✅ Game state reset complete, loading save...');

      // Load game state into stores
      updateGameDate(save.gameDate);
      setCash(save.company.metrics.cash);
      updateMetrics({
        reputation: save.company.metrics.reputation,
        marketShare: save.company.metrics.marketShare
      });
      setResearchPoints(save.company.metrics.researchPoints);
      setBankruptcyWarnings(save.company.metrics.bankruptcyWarnings);
      setIsGameOver(save.company.metrics.isGameOver);

      // Restore full financial state if available
      if (save.company.finances) {
        useCompanyStore.getState().updateFinances(save.company.finances);
      }

      // Load products
      const validProducts = save.company.products.map(p => ({
        ...p,
        status: p.status,
        designConfig: {
          ...p.designConfig,
          components: {
            ...p.designConfig.components,
            os: p.designConfig.components.os || 'default'
          }
        }
      })) as Product[];

      setProducts(validProducts);
      setProducts(validProducts);

      // Clean up any orphaned employee allocations after loading
      if (save.company.employeeAllocations && save.company.employeeAllocations.length > 0) {
        const cleanedAllocations = employeeAllocationService.cleanupOrphanedAllocations(
          validProducts,
          save.company.employeeAllocations
        );

        if (cleanedAllocations.length !== save.company.employeeAllocations.length) {
          // console.log(`Cleaned up ${save.company.employeeAllocations.length - cleanedAllocations.length} orphaned employee allocations`);
          setEmployeeAllocations(cleanedAllocations);
        } else {
          setEmployeeAllocations(save.company.employeeAllocations);
        }
      }

      // Load tutorial state BEFORE showing game dashboard to prevent race condition
      if (save.settings?.tutorialCompleted !== undefined) {
        useUIStore.getState().setTutorialCompleted(save.settings.tutorialCompleted);
      }
      if (save.settings?.tutorialSkipped !== undefined) {
        useUIStore.getState().setTutorialSkipped(save.settings.tutorialSkipped);
      }

      setCurrentSlotId(slotId);
      setShowWelcome(false);
      setShowSettings(false);
      setCompanyInfo({
        ceoName: save.company.ceoName,
        companyName: save.company.name
      });

      // Ensure game starts paused after loading
      pauseGame();

      // Update company store
      updateCompany({
        id: save.company.id,
        name: save.company.name,
        ceoName: save.company.ceoName,
        founded: save.company.founded,
        productCount: save.company.products.length,
        isPlayer: true
      });

      // Load phase preferences from save file
      if (save.settings?.phaseProgressionPreferences) {
        setPhasePreferences(save.settings.phaseProgressionPreferences);
      }

      // Load buildings
      if (save.company.buildings) {
        setBuildings(save.company.buildings);
      }

      // Load employees and hiring processes
      if (save.company.employees) {
        setEmployees(save.company.employees);
      }
      if (save.company.hiringProcesses) {
        setHiringProcesses(save.company.hiringProcesses);
      }

      // Load fire warning preference
      if (save.settings?.fireWarningEnabled !== undefined) {
        toggleFireWarning(save.settings.fireWarningEnabled);
      }

      // Load audio settings
      if (save.settings?.audio) {
        setAudioSettings(save.settings.audio);
        audioService.applySettings(save.settings.audio);
      }

      // Load event state
      if (save.eventState) {
        // Restore event history and pending events
        const { eventStore } = await import('../services/tycoon/eventStore');
        const { eventEngine } = await import('../services/tycoon/eventEngine');

        // Restore event history
        if (save.eventState.eventHistory) {
          eventStore.restoreEventHistory(save.eventState.eventHistory);
        }

        // Restore pending reactive events
        if (save.eventState.pendingReactiveEvents) {
          for (const event of save.eventState.pendingReactiveEvents) {
            eventEngine.restorePendingEvent(event);
          }
        }

        // Restore market conditions
        if (save.eventState.marketConditions) {
          const { marketModifierService } = await import('../services/tycoon/marketModifierService');
          marketModifierService.restoreConditions(save.eventState.marketConditions);
        }
      }

      // Load production batches
      if (save.productionBatches && save.productionBatches.length > 0) {
        try {
          const { ProductionService } = await import('../services/ProductionService');
          await ProductionService.restoreBatches(save.productionBatches);
        } catch (error) {
          // console.warn('Failed to restore production batches:', error);
        }
      }

      // Load inventory state
      if (save.inventoryState) {
        try {
          // console.log('📦 Restoring inventory state...');
          const { DatabaseService } = await import('../services/DatabaseService');

          // Clear existing inventory data
          await DatabaseService.clear('component_inventory');
          await DatabaseService.clear('device_inventory');
          await DatabaseService.clear('component_orders');

          // Restore component inventory
          if (save.inventoryState.components) {
            for (const component of save.inventoryState.components) {
              await DatabaseService.add('component_inventory', component);
            }
            // console.log(`✅ Restored ${save.inventoryState.components.length} component inventory entries`);
          }

          // Restore device inventory
          if (save.inventoryState.devices) {
            for (const device of save.inventoryState.devices) {
              await DatabaseService.add('device_inventory', device);
            }
            // console.log(`✅ Restored ${save.inventoryState.devices.length} device inventory entries`);
          }

          // Restore pending orders
          if (save.inventoryState.pendingOrders) {
            for (const order of save.inventoryState.pendingOrders) {
              await DatabaseService.add('component_orders', order);
            }
            // console.log(`✅ Restored ${save.inventoryState.pendingOrders.length} pending component orders`);
          }

          // Restore auto-order settings to inventory store
          if (save.inventoryState.autoOrderSettings) {
            useInventoryStore.getState().hydrateFromSave({
              autoOrderSettings: save.inventoryState.autoOrderSettings
            });
            // console.log('✅ Restored auto-order settings');
          }
        } catch (error) {
          // console.warn('Failed to restore inventory state:', error);
        }
      } else {
        // If no inventory state in save file (older saves), initialize components
        // console.log('📦 No inventory state in save file, initializing component inventory...');
        const { inventoryManagementService } = await import('../services/tycoon/inventoryManagementService');
        await inventoryManagementService.initializeAllComponentInventory();
        // console.log('✅ Component inventory initialized for legacy save');
      }

      // Restore production lines
      if (save.productionLines && save.productionLines.length > 0) {
        useProductionStore.getState().hydrateFromSave({
          productionBatches: save.productionBatches,
          factories: save.company.factories,
          productionLines: save.productionLines
        });
        // console.log(`✅ Restored ${save.productionLines.length} production lines`);
      }

      // Load supply/demand history
      if (save.supplyDemandHistory) {
        useSupplyDemandHistoryStore.getState().hydrateFromSave(save.supplyDemandHistory);
      }

      // Load financial history
      if (save.financialHistory) {
        useFinancialHistoryStore.getState().hydrateFromSave(save.financialHistory);
      }

      // Restore current month accumulator
      if (save.currentMonthAccumulator) {
        monthlyAccumulatorRef.current = save.currentMonthAccumulator;
      } else {
        monthlyAccumulatorRef.current = { revenue: 0, costs: 0 };
      }

      // Load research state
      if (save.company.research) {
        setCurrentResearchState(save.company.research);
      }

      // Restore competitor products
      if (save.competitorProducts && save.competitorProducts.length > 0) {
        try {
          // console.log(`📦 Restoring ${save.competitorProducts.length} competitor products...`);

          // Import ProductManager
          const { ProductManager } = await import('../services/productManager');

          // Clear existing competitor products first
          await ProductManager.clearCompetitorProducts();

          // Reset competitor AI to prevent it from creating new products
          const { competitorAI } = await import('../services/tycoon/competitorAIService');
          competitorAI.resetForNewGame();

          // Add each competitor product back to the database
          for (const product of save.competitorProducts) {
            const productData = {
              ...product,
              id: undefined // Let the database generate a new ID
            };
            await ProductManager.createProduct(productData);
          }

          // console.log(`✅ Successfully restored ${save.competitorProducts.length} competitor products`);
        } catch (error) {
          // console.error('Failed to restore competitor products:', error);
        }
      }

      // Restore market state services
      if (save.marketState) {
        // Update market simulator with saved state
        const { MarketSimulatorService } = await import('../services/tycoon/marketSimulatorService');
        const marketSimulator = MarketSimulatorService.getInstance();
        // For now, just log the market state restoration
        // console.log('Restoring market state:', save.marketState);
      }
    } catch (error) {
      // console.error('❌ Failed to load game:', error);
      setError('Failed to load game. Please try again.');
    }
  };

  const handleCloseBankruptcyWarning = () => {
    setShowBankruptcyWarning(false);
    resumeGame();
  };

  const handleReturnToMenu = async () => {
    // console.log('🏠 Returning to menu - cleaning up sync operations...');

    // Pause sync operations and clear queues for all stores before resetting
    try {
      // Get sync controls from stores that have them
      const gameStoreSync = useGameStore.getState().sync;
      const companyStoreSync = useCompanyStore.getState().sync;
      const productStoreSync = useProductStore.getState().sync;
      const marketStoreSync = useMarketStore.getState().sync;
      const buildingStoreSync = useBuildingStore.getState().sync;

      // Pause all sync operations (this will also cancel any scheduled syncs)
      if (gameStoreSync?.pauseSync) gameStoreSync.pauseSync();
      if (companyStoreSync?.pauseSync) companyStoreSync.pauseSync();
      if (productStoreSync?.pauseSync) productStoreSync.pauseSync();
      if (marketStoreSync?.pauseSync) marketStoreSync.pauseSync();
      if (buildingStoreSync?.pauseSync) buildingStoreSync.pauseSync();

      // Clear all sync queues
      if (gameStoreSync?.clearSyncQueue) gameStoreSync.clearSyncQueue();
      if (companyStoreSync?.clearSyncQueue) companyStoreSync.clearSyncQueue();
      if (productStoreSync?.clearSyncQueue) productStoreSync.clearSyncQueue();
      if (marketStoreSync?.clearSyncQueue) marketStoreSync.clearSyncQueue();
      if (buildingStoreSync?.clearSyncQueue) buildingStoreSync.clearSyncQueue();

      // console.log('✅ Sync operations paused and queues cleared');

      // Wait for any in-flight sync operations to complete
      await Promise.all([
        gameStoreSync?.syncNow?.().catch(() => { }),
        companyStoreSync?.syncNow?.().catch(() => { }),
        productStoreSync?.syncNow?.().catch(() => { }),
        marketStoreSync?.syncNow?.().catch(() => { }),
        buildingStoreSync?.syncNow?.().catch(() => { })
      ]);

      // Additional delay to ensure all async operations have settled
      await new Promise(resolve => setTimeout(resolve, 200));

      // console.log('✅ All pending sync operations completed');
    } catch (error) {
      // console.warn('⚠️ Error cleaning up sync operations:', error);
    }

    // Now reset the stores
    resetGame();
    resetCompany();
    setCompanyInfo(null);
    setError(null);
    setShowBankruptcyWarning(false);
    setShowWelcome(true);
    setShowNewGame(false);
    setBankruptcyWarnings(0);
    setIsGameOver(false);
    setEvents([]);
    setBuildings([]);
    setHiringProcesses([]);
    setEmployeeAllocations([]);

    // Clear player products when returning to menu
    // console.log('🏠 Returning to menu - clearing player products...');
    setProducts([]);
    setProducts([]);
    // console.log('✅ Player products cleared');

    // Clear building state when returning to menu
    setBuildings([]);
    // console.log('✅ Building state cleared');

    // Reset all game state for fresh start
    try {
      // console.log('🏠 Returning to menu - resetting all game state...');
      const { GameStateResetManager } = await import('../services/tycoon/gameStateResetManager');
      await GameStateResetManager.resetAllGameState();
      // console.log('✅ All game state reset for fresh start');
    } catch (error) {
      // console.error('❌ Failed to reset game state:', error);
    }

    // Resume sync operations after reset
    try {
      const gameStoreSync = useGameStore.getState().sync;
      const companyStoreSync = useCompanyStore.getState().sync;
      const productStoreSync = useProductStore.getState().sync;
      const marketStoreSync = useMarketStore.getState().sync;
      const buildingStoreSync = useBuildingStore.getState().sync;

      if (gameStoreSync?.resumeSync) gameStoreSync.resumeSync();
      if (companyStoreSync?.resumeSync) companyStoreSync.resumeSync();
      if (productStoreSync?.resumeSync) productStoreSync.resumeSync();
      if (marketStoreSync?.resumeSync) marketStoreSync.resumeSync();
      if (buildingStoreSync?.resumeSync) buildingStoreSync.resumeSync();

      // console.log('✅ Sync operations resumed');
    } catch (error) {
      // console.warn('⚠️ Error resuming sync operations:', error);
    }
  };

  // Check if we have enough employees to start a new product
  const checkEmployeeAvailability = useCallback((techScore: number = 50) => {
    const { minEmployeesPerPhase, employeeScaling } = EMPLOYEE_CONSTANTS;

    // Calculate scaling factors
    const techMultiplier = Math.min(
      employeeScaling.techComplexityBase + (techScore * employeeScaling.techComplexityFactor),
      employeeScaling.maxTechMultiplier
    );

    const yearsSince2000 = gameDate.year - 2000;
    const timeMultiplier = Math.min(
      employeeScaling.timeProgressionBase + (yearsSince2000 * employeeScaling.timeProgressionFactor),
      employeeScaling.maxTimeMultiplier
    );

    const totalMultiplier = techMultiplier * timeMultiplier;

    // Check availability for all phases
    const missing: Record<string, number> = {};
    let hasEnough = true;

    Object.entries(minEmployeesPerPhase).forEach(([phase, roles]) => {
      Object.entries(roles).forEach(([role, baseCount]) => {
        const required = Math.ceil(baseCount * totalMultiplier);
        const available = employees.filter(
          emp => emp.role === role
        ).length;

        if (available < required) {
          missing[role] = (missing[role] || 0) + (required - available);
          hasEnough = false;
        }
      });
    });

    return { hasEnough, missing };
  }, [gameDate.year, employees]);

  // Handle employee allocation confirmation
  const handleAllocationConfirm = useCallback(async (allocations: PlannedEmployeeAllocations) => {
    if (!pendingDesignConfig) return;

    // console.log('[TycoonMode] handleAllocationConfirm - Current allocations before creation:', employeeAllocations);

    // Start development with planned allocations
    const result = await startDevelopment(
      { ...pendingDesignConfig, plannedAllocations: allocations },
      cash,
      (newCash) => setCash(newCash),
      gameDate,
      phasePreferences
    );

    if (result.success && result.product) {
      setShowAllocationModal(false);
      setPendingDesignConfig(null);

      // Force a small delay to ensure state updates are propagated
      setTimeout(() => {
        // console.log('[TycoonMode] Allocations after product creation:', employeeAllocations);
      }, 100);
    } else {
      // Show error message to user
      setError(result.error || 'Failed to create product');
      setShowAllocationModal(false);
      setPendingDesignConfig(null);
    }
  }, [pendingDesignConfig, startDevelopment, cash, setCash, gameDate, phasePreferences, employeeAllocations]);

  const handleProductAction = useCallback((productId: string, action: string) => {
    if (action === 'develop') {
      // Check employee availability before allowing new product development
      const { hasEnough, missing } = checkEmployeeAvailability();

      if (!hasEnough) {
        // Show warning modal
        setMissingEmployees(missing);
        setShowEmployeeWarning(true);
      } else {
        // Store current pause state and pause the game before entering designer
        wasPausedBeforeDesignerRef.current = isPaused;
        pauseGame();
        // Navigate to designer route
        setDesignerRouteState(null); // Clear any previous state
        navigate('/designer');
      }
    } else if (action === 'produce') {
      startProduction(productId, gameDate);
    } else if (action === 'discontinue') {
      // Open confirmation modal instead of directly discontinuing
      const product = products.find(p => p.id === productId);
      if (product) {
        setProductToDiscontinue(product);
        setDiscontinueModalOpen(true);
      }
    } else if (action === 'discontinue-confirmed') {
      // Actually discontinue the product after user confirms
      discontinueProduct(productId, gameDate);
    } else if (action === 'release') {
      releaseProduct(productId, productReviews);
    } else if (action === 'edit') {
      // Store current pause state and pause the game before entering designer
      wasPausedBeforeDesignerRef.current = isPaused;
      pauseGame();
      // Navigate to designer route with product to edit
      const productToEdit = products.find(p => p.id === productId);
      setDesignerRouteState({ editProduct: productToEdit });
      navigate('/designer');
    }
  }, [navigate, startProduction, discontinueProduct, releaseProduct, products, gameDate,
    companyInfo, checkEmployeeAvailability, isPaused, pauseGame]);

  const handleReleaseModalChange = useCallback((open: boolean, product: ProductType | null, reviews: ProductReviews) => {
    setReleaseModalOpen(open);
    setProductToRelease(product);
    setProductReviews(reviews);
  }, []);

  const handleDiscontinueModalChange = useCallback((open: boolean, product: ProductType | null) => {
    setDiscontinueModalOpen(open);
    setProductToDiscontinue(product);
  }, []);

  // Handle research state updates
  const handleResearchStateChange = useCallback((researchState: ResearchState) => {
    setCurrentResearchState(researchState);
    // Also update the company store
    useCompanyStore.getState().updateResearch(researchState);

    // Extract research unlocks when research state changes
    if (researchState && researchState.unlockedNodes) {
      import('../data/researchTree').then(({ researchTree }) => {
        const allNodes = researchTree.branches.flatMap(branch => branch.nodes);
        const unlocks: any[] = [];

        researchState.unlockedNodes.forEach((nodeId: string) => {
          const node = allNodes.find(n => n.id === nodeId);
          if (node) {
            unlocks.push(...node.unlocks);
          }
        });

        setResearchUnlocks(unlocks);
      });
    }
  }, []);

  // Structure the gameState properly for the dashboard - memoized to prevent recreating on every render
  const gameState = useMemo(() => {
    // console.log(`[${new Date().toISOString()}] [TycoonMode.gameState] Creating gameState with companyState.cash:`, state?.cash);
    return {
      companyState: state,
      gameControls: {
        isPaused: isPaused,
        gameSpeed: simulationSpeed
      },
      events: events,
      addEvent: (event: any) => addEvent(event),
      activeEvents: eventsData.pendingReactiveEvents || [],
      products: products,
      fireWarningEnabled: showFireWarning,
      onHire: (role: string, count: number) => startHiring({
        role,
        count,
        gameDate,
        checkCapacity: checkCapacityForHiring,
        onEvent: addEvent
      }),
      onFire: (role: string, count: number) => fireEmployees({
        role,
        count,
        onEvent: addEvent
      }),
      onEmployeeUpdate: (employees: any[]) => setEmployees(employees),
      onHiringUpdate: (processes: any[]) => setHiringProcesses(processes),
      onToggleFireWarning: (show: boolean) => toggleFireWarning(show),
      handleEventAction: (eventId: string, action: string) => {
        // console.log('Event action:', eventId, action);
        // Handle event actions here
      },
      onResearchStateChange: (researchState: any) => handleResearchStateChange(researchState),
      updateCompanyState: (updates: any) => updateState(updates),
      buildingManagement: buildingManagement,
      // Add buildings and onBuildingsUpdate for proper state management
      buildings: buildings,
      onBuildingsUpdate: (buildings: OwnedBuilding[]) => {
        // console.log('🏢 Updating buildings in main state');
        updateState({ buildings });
      }
    };
  }, [state, isPaused, simulationSpeed, events, eventsData.pendingReactiveEvents, products,
    showFireWarning, buildingManagement, buildings, gameDate, checkCapacityForHiring,
    startHiring, fireEmployees, setEmployees, setHiringProcesses, toggleFireWarning]);

  // Report game state to dev mode - using same ref pattern to prevent loops
  const lastGameStateRef = useRef<string>('');
  useEffect(() => {
    if (onGameStateUpdate && !showWelcome && companyInfo) {
      // Create a hash of the game state to detect changes
      const gameStateHash = JSON.stringify({
        cash,
        date: `${gameDate.year}-${gameDate.month}-${gameDate.day}`,
        isPaused,
        gameSpeed: simulationSpeed,
        eventCount: events.length,
        productCount: products.length,
        activeEventCount: eventsData.pendingReactiveEvents?.length || 0,
        fireWarningEnabled: showFireWarning,
      });

      // Only update if game state has actually changed
      if (gameStateHash !== lastGameStateRef.current) {
        lastGameStateRef.current = gameStateHash;
        onGameStateUpdate(gameState);
      }
    }
  }, [state, isPaused, simulationSpeed, events, eventsData.pendingReactiveEvents, products,
    showWelcome, companyInfo, onGameStateUpdate, gameState, showFireWarning]);

  const productPipeline = {
    products,
    actions: {
      startDevelopment,
      startProduction,
      discontinueProduct,
      releaseProduct,
      updateProductSales,
      setProducts
    },
    utils: {
      getPhaseProgress: (product: Product) => getPhaseProgress(product.id),
      isPhaseComplete
    }
  };

  return (
    <Routes>
      <Route path="" element={
        <>
        {showWelcome ? (
          showNewGame ? (
            <WelcomePopup onStart={handleStartGame} onBack={() => setShowNewGame(false)} />
          ) : (
            <MainMenuScreen
              onNewGame={() => setShowNewGame(true)}
              onLoadGame={handleLoadGame}
              onDesignerStudio={() => navigate('/designer')}
              onCommunityHub={() => onCommunityHub?.()}
              onDevMode={() => onDevMode?.()}
            />
          )
        ) : (
          <>
            {/* Error banner */}
            {error && (
              <div className="fixed top-0 left-0 right-0 z-50 bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                  <div className="ml-auto pl-3">
                    <button
                      onClick={() => setError(null)}
                      className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Dashboard Layout with Tutorial */}
            <div data-testid="tycoon-dashboard" className="w-full h-screen">
              <TutorialProvider
                currentTab={navigationMode === 'spatial' ? spatialCurrentTab : dashboardActiveTab}
                onTabChange={(tab) => {
                  // This will be called when the tutorial needs to switch tabs
                  if (navigationMode === 'spatial') {
                    navigateToTab(tab);
                  } else {
                    setDashboardActiveTab(tab);
                  }
                }}
                onResumeGame={resumeGame}
                isLoadedGame={currentSlotId !== null}>
                {navigationMode === 'spatial' ? (
                  <SpatialGameLayout
                    gameState={gameState}
                    productPipeline={productPipeline}
                    onBack={() => setShowSettings(true)}
                    companyName={companyInfo?.companyName || 'Your Company'}
                    ceoName={companyInfo?.ceoName || 'You'}
                    onTogglePause={togglePause}
                    onChangeSpeed={changeSpeed}
                    availableSpeeds={GAME_SPEEDS}
                    onProductAction={handleProductAction}
                    releaseModalOpen={releaseModalOpen}
                    productToRelease={productToRelease}
                    productReviews={productReviews}
                    onReleaseModalChange={handleReleaseModalChange}
                    discontinueModalOpen={discontinueModalOpen}
                    productToDiscontinue={productToDiscontinue}
                    onDiscontinueModalChange={handleDiscontinueModalChange}
                  />
                ) : (
                  <GameDashboard
                    gameState={gameState}
                    productPipeline={productPipeline}
                    onBack={() => setShowSettings(true)}
                    companyName={companyInfo?.companyName || 'Your Company'}
                    ceoName={companyInfo?.ceoName || 'You'}
                    onTogglePause={togglePause}
                    onChangeSpeed={changeSpeed}
                    availableSpeeds={GAME_SPEEDS}
                    onProductAction={handleProductAction}
                    releaseModalOpen={releaseModalOpen}
                    productToRelease={productToRelease}
                    productReviews={productReviews}
                    onReleaseModalChange={handleReleaseModalChange}
                    discontinueModalOpen={discontinueModalOpen}
                    productToDiscontinue={productToDiscontinue}
                    onDiscontinueModalChange={handleDiscontinueModalChange}
                    activeTab={dashboardActiveTab}
                    onTabChange={setDashboardActiveTab}
                  />
                )}
              </TutorialProvider>
            </div>

            <SettingsModal
              isOpen={showSettings}
              onClose={() => setShowSettings(false)}
              onSave={handleSaveGame}
              onReturnToMenu={handleReturnToMenu}
              onRestartTutorial={handleRestartTutorial}
              isSaving={isSaving}
              saveSuccess={saveSuccess}
              phasePreferences={phasePreferences}
              onPhasePreferenceChange={handlePhasePreferenceChange}
              fireWarningEnabled={showFireWarning}
              onFireWarningChange={toggleFireWarning}
              audioSettings={audioSettings}
              onAudioSettingsChange={(updates) => {
                const newSettings = { ...audioSettings, ...updates };
                setAudioSettings(newSettings);

                // Save to localStorage
                const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
                const prefs = JSON.parse(savedPrefs);
                prefs.musicEnabled = newSettings.musicEnabled;
                prefs.musicVolume = newSettings.musicVolume;
                prefs.currentTrack = newSettings.currentTrack;
                localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));
              }}
            />

            <GameModals
              showBankruptcyWarning={showBankruptcyWarning}
              bankruptcyWarnings={bankruptcyWarnings}
              isGameOver={isGameOver}
              onCloseBankruptcyWarning={handleCloseBankruptcyWarning}
              onReturnToMenu={handleReturnToMenu}
            />

            {/* Dynamic Events UI */}
            <ReactiveEventModal
              event={eventsData.pendingReactiveEvents?.[0] || null}
              isOpen={(eventsData.pendingReactiveEvents?.length || 0) > 0}
              playerCash={cash}
              error={error}
              onClearError={handleClearError}
              onChoice={(choiceId, dontAskAgain) => {
                const pendingEvent = eventsData.pendingReactiveEvents?.[0];
                if (pendingEvent) {
                  // Use the enhanced handler for phase progression events
                  if (pendingEvent.event.category === 'development' && pendingEvent.productContext) {
                    return handlePhaseEventChoice(pendingEvent.event.id, choiceId, dontAskAgain);
                  } else if (eventsActions && eventsActions.handlePlayerChoice) {
                    return eventsActions.handlePlayerChoice(pendingEvent.event.id, choiceId);
                  }
                }
              }}
              onClose={() => {
                // In this implementation, we require a choice, so we auto-select the last option
                const pendingEvent = eventsData.pendingReactiveEvents?.[0];
                if (pendingEvent && pendingEvent.event.choices) {
                  const lastChoice = pendingEvent.event.choices[pendingEvent.event.choices.length - 1];
                  if (pendingEvent.event.category === 'development' && pendingEvent.productContext) {
                    handlePhaseEventChoice(pendingEvent.event.id, lastChoice.id);
                  } else if (eventsActions && eventsActions.handlePlayerChoice) {
                    eventsActions.handlePlayerChoice(pendingEvent.event.id, lastChoice.id);
                  }
                }
              }}
            />

            <SystemicEventNotifications
              events={eventsData.systemicNotifications}
              onDismiss={eventsActions && eventsActions.dismissSystemicNotification ? eventsActions.dismissSystemicNotification : () => { }}
            />

            {/* Employee allocation modals */}
            <EmployeeAvailabilityWarning
              isOpen={showEmployeeWarning}
              onClose={() => setShowEmployeeWarning(false)}
              onProceedAnyway={() => {
                setShowEmployeeWarning(false);
                // Store current pause state and pause the game before entering designer
                wasPausedBeforeDesignerRef.current = isPaused;
                pauseGame();
                setDesignerRouteState(null);
                navigate('/designer');
              }}
              missingEmployees={missingEmployees}
              techScore={50} // Default tech score estimate
              currentYear={gameDate.year}
            />

            {/* Debug logging for employee allocations */}
            {/* {showAllocationModal && console.log('[TycoonMode] Current allocations being passed to modal:', latestAllocationsRef.current)} */}
            <EmployeeAllocationModal
              key={modalKey} // Force re-mount to get fresh state
              isOpen={showAllocationModal}
              onClose={() => {
                setShowAllocationModal(false);
                setPendingDesignConfig(null);
              }}
              onConfirm={handleAllocationConfirm}
              employees={employees}
              currentAllocations={employeeAllocations}
              productName={pendingDesignConfig?.name || 'New Product'}
              techScore={pendingDesignConfig?.techScore || 50}
              currentYear={gameDate.year}
            />

            {/* Version number - hidden during spatial/campus mode */}
            {navigationMode !== 'spatial' && (
              <div className="fixed bottom-0 right-0 z-40 py-4 px-4">
                <div className="text-right">
                  <div className="text-sm text-white/60">v{packageJson.version}</div>
                  <div className="text-xs text-white/40">{(packageJson as any).buildNumber || 'dev'}</div>
                </div>
              </div>
            )}
          </>
        )}
        </>
      } />
      <Route path="/designer" element={
        <>
        {showWelcome ? (
          // Standalone Designer Studio mode when accessed from main menu
          <DesignerStudio
            onBack={() => navigate('/')}
            developmentMode={false}
            budget={0}
            gameDate={undefined}
            completedResearch={[]}
            researchUnlocks={[]}
            fromTycoon={false}
            companyName=""
            productCount={0}
            editProduct={null}
            onNewDesign={undefined}
            onUpdateProduct={undefined}
          />
        ) : (
          // In-game Designer Studio mode
          <DesignerStudio
            onBack={() => {
              setDesignerRouteState(null); // Clear state when going back
              // Resume game if it wasn't paused before entering designer
              if (!wasPausedBeforeDesignerRef.current) {
                resumeGame();
              }
              navigate(-1);
            }}
            developmentMode={true}
            budget={cash}
            gameDate={gameDate}
            completedResearch={currentResearchState?.unlockedNodes || []}
            researchUnlocks={researchUnlocks}
            fromTycoon={true}
            companyName={companyInfo?.companyName}
            productCount={products.length}
            editProduct={designerRouteState?.editProduct}
            onNewDesign={async (designConfig) => {
              // console.log('[TycoonMode] onNewDesign - Current allocations:', employeeAllocations);
              // console.log('[TycoonMode] onNewDesign - Latest allocations ref:', latestAllocationsRef.current);
              // Store the design config and show allocation modal
              setPendingDesignConfig(designConfig);
              setModalKey(prev => prev + 1); // Force modal to re-mount
              setShowAllocationModal(true);
              // Resume game if it wasn't paused before entering designer
              if (!wasPausedBeforeDesignerRef.current) {
                resumeGame();
              }
              // Navigate back immediately - modal will handle the rest
              navigate(-1);
            }}
            onUpdateProduct={async (updatedProduct) => {
              // Handle product updates
              const productIndex = products.findIndex(p => p.id === updatedProduct.id);
              if (productIndex !== -1) {
                const newProducts = [...products];
                newProducts[productIndex] = updatedProduct;
                setProducts(newProducts);
                setDesignerRouteState(null); // Clear state after success
                // Resume game if it wasn't paused before entering designer
                if (!wasPausedBeforeDesignerRef.current) {
                  resumeGame();
                }
                navigate(-1);
              }
            }}
          />
        )}
        </>
      } />
    </Routes>
  );
}
